import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Users, CornerDownRight, ChevronDown, Receipt, Plus, Trash2, Star, ChevronRight, Building2, Check, X, Newspaper, Pin, Edit2, Eye, Cloud, AlertCircle, LayoutGrid, Search, KeyRound, ExternalLink, ArrowRight, Mail, Camera, CalendarDays, Briefcase } from 'lucide-react';
import Shell from '../components/TopBarShell';
import StatusBadge from '../components/StatusBadge';
import OdooBadge from '../components/OdooBadge';
import EmployeeAvatar from '../components/EmployeeAvatar';
import { Button } from '../components/ui/button';
import AnnouncementComposer from '../components/announcements/AnnouncementComposer';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useReviewQueue } from '../contexts/ReviewQueueContext';
import { useLocale, useT } from '../contexts/LocaleProvider';
import { deptLabel, setDepartmentCatalogue, useDepartments } from '../lib/departments';
import api from '../utils/api';

const fmtIban = (s) => ((s || '').replace(/\s+/g, '').toUpperCase().match(/.{1,4}/g) || []).join(' ');
const fmtMoney = (n) => `€ ${Number(n || 0).toFixed(2)}`;

/* ---------------------------------- Employees ---------------------------- */

/* Inline editor for free-text employee fields · commits on blur or
 * Enter so we don't fire a PATCH on every keystroke. Local state
 * stays in sync if the parent's value changes (e.g. after refetch). */
/* Employment status · deactivate someone who left (they disappear from the
 * directory, team, search and surveys, and can no longer sign in; their
 * expense notes and history stay), or reactivate them. */
function EmployeeStatus({ emp, onPatch, t }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="mb-5 pb-4 border-b border-border">
      <div className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">
        {t('admin.status.section')}
      </div>
      {emp.is_active ? (
        confirm ? (
          <div className="flex flex-wrap items-center gap-3 text-[13px]">
            <span style={{ color: 'hsl(var(--text))' }}>{t('admin.deactivate.confirm').replace('{name}', emp.full_name)}</span>
            <Button size="sm" variant="destructive" onClick={() => { onPatch(emp.id, { is_active: false }); setConfirm(false); }}>
              {t('admin.deactivate')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirm(false)}>{t('common.cancel')}</Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[13px]" style={{ color: 'hsl(var(--text))' }}>{t('admin.status.active')}</span>
            <Button size="sm" variant="outline" onClick={() => setConfirm(true)}>{t('admin.deactivate')}</Button>
          </div>
        )
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[13px]" style={{ color: 'hsl(var(--danger))' }}>{t('admin.status.inactive')}</span>
          <Button size="sm" variant="outline" onClick={() => onPatch(emp.id, { is_active: true })}>{t('admin.reactivate')}</Button>
        </div>
      )}
      <p className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1.5 max-w-[640px]">{t('admin.deactivate.hint')}</p>
    </div>
  );
}

const PosteEditor = ({ value, onCommit, placeholder, ...rest }) => {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  const commit = () => {
    const next = draft.trim();
    if (next !== (value || '').trim()) onCommit(next);
  };
  return (
    <input
      type="text"
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.currentTarget.blur(); }
        else if (e.key === 'Escape') { setDraft(value || ''); e.currentTarget.blur(); }
      }}
      className="w-full text-[13px] py-1 px-1.5 rounded border border-transparent hover:border-border focus:border-[hsl(var(--ring))] focus:outline-none bg-transparent"
      {...rest}
    />
  );
};

/* "Last seen" cell for the Medewerkers table · reads the per-employee
 * portal-visit summary (see backend EmployeeActivityView). No entry means
 * the collaborator has never signed in since tracking began — shown red so
 * HR can spot them. Stale (>30 d) is amber; recent is muted. The tooltip
 * carries the exact date plus visit counts. */
const _dayMs = 24 * 60 * 60 * 1000;
const LastSeenCell = ({ t, act }) => {
  if (!act || !act.last_seen) {
    return (
      <span className="inline-flex items-center gap-1 text-[12px] text-[hsl(var(--danger))]"
            title={t('admin.activity.neverHint')}>
        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--danger))]" />
        {t('admin.activity.never')}
      </span>
    );
  }
  const seen = new Date(act.last_seen + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((today - seen) / _dayMs);
  const label = days <= 0 ? t('admin.activity.today')
    : days === 1 ? t('admin.activity.yesterday')
    : days < 30 ? t('admin.activity.daysAgo', { n: days })
    : seen.toLocaleDateString();
  const color = days >= 30 ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--text-soft))]';
  const tip = `${seen.toLocaleDateString()} · ${t('admin.activity.totalDays', { n: act.total_days })} · ${t('admin.activity.last30', { n: act.days_30 })}`;
  return <span className={`text-[12px] font-num ${color}`} title={tip}>{label}</span>;
};

const EmployeeRow = ({ emp, locale, t, departments, allEmployees, onPatch, onAddBank, onUpdateBank, onDeleteBank, onActAs, highlightExternal = false,
                       apps, onLoadApps, onSetAppGrant, activity = null }) => {
  const [open, setOpen] = useState(false);
  const [newIban, setNewIban] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [bankErr, setBankErr] = useState('');
  // First time the row is expanded, ask the parent to load the apps
  // catalogue if it hasn't yet. Cheap if already loaded.
  useEffect(() => {
    if (open && onLoadApps) onLoadApps();
  }, [open, onLoadApps]);
  // Sort active apps by group then name so the list is scannable.
  const activeApps = useMemo(
    () => (apps || []).filter(a => a.is_active).slice().sort((a, b) => (
      (a.group || '').localeCompare(b.group || '') || (a.name || '').localeCompare(b.name || '')
    )),
    [apps],
  );
  const grantedCount = useMemo(
    () => activeApps.reduce((n, a) => n + ((a.allowed_employees || []).includes(emp.id) ? 1 : 0), 0),
    [activeApps, emp.id],
  );

  const addBank = async () => {
    setBankErr('');
    if (!newIban.trim()) { setBankErr(t('bank.ibanRequired')); return; }
    try {
      await onAddBank(emp.id, { iban: newIban.trim(), label: newLabel.trim() });
      setNewIban(''); setNewLabel('');
    } catch (e) {
      const d = e.response?.data;
      setBankErr(Array.isArray(d?.iban) ? d.iban[0] : (d?.detail || t('bank.saveFailed')));
    }
  };

  // In the mixed "Alle" view, externals get a subtle accent so they
  // pop out of the otherwise-uniform row stream. We use a left edge
  // colour bar + faint surface tint · keeps the table grid intact
  // while still being legible from across the room.
  const highlight = highlightExternal && emp.is_external;
  return (
    <>
      <tr className={`border-t border-[hsl(var(--rule))] hover:bg-[hsl(var(--surface-2))] ${highlight ? 'bg-[hsl(var(--accent-soft))]/40' : ''}`}
          style={highlight ? { boxShadow: 'inset 3px 0 0 hsl(var(--accent))' } : undefined}>
        <td className="px-3 py-2 min-w-0">
          <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 min-w-0 text-left w-full">
            <ChevronRight className={`h-4 w-4 shrink-0 text-[hsl(var(--text-muted))] transition ${open ? 'rotate-90' : ''}`} />
            <EmployeeAvatar fullName={emp.full_name} email={emp.email} photo={emp.photo} size={28} />
            <span className="font-medium truncate">{emp.full_name}</span>
            {emp.is_external && (
              <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full shrink-0"
                    style={{ background: 'hsl(var(--bg-subtle))', color: 'hsl(var(--text-soft))' }}
                    title={t('admin.employees.externalBadge.help')}>
                {t('admin.employees.externalBadge')}
              </span>
            )}
            {!emp.is_active && <span className="text-[11px] text-[hsl(var(--text-muted))] shrink-0">({t('admin.inactive')})</span>}
          </button>
        </td>
        <td className="px-3 py-2 font-num text-[12.5px] text-[hsl(var(--text-soft))] truncate"
            title={emp.email}>{emp.email}</td>
        <td className="px-3 py-2">
          <select
            value={emp.departement || ''}
            onChange={(e) => onPatch(emp.id, { departement: e.target.value })}
            className="text-[12.5px] py-1 w-full"
          >
            <option value="">{t('admin.noDept')}</option>
            {departments.map(d => <option key={d.code} value={d.code}>{deptLabel(d.code, locale)}</option>)}
          </select>
        </td>
        <td className="px-3 py-2 min-w-0">
          <PosteEditor
            value={emp.poste || ''}
            onCommit={(v) => onPatch(emp.id, { poste: v })}
            placeholder={t('admin.poste.ph') || '—'}
            aria-label={t('directory.col.role')}
            title={emp.poste || ''}
          />
        </td>
        <td className="px-3 py-2">
          <select
            value={emp.manager_email || ''}
            onChange={(e) => onPatch(emp.id, { manager_email: e.target.value || null })}
            className="text-[12.5px] py-1 w-full"
            aria-label={t('admin.manager')}
          >
            <option value="">{t('admin.noManager')}</option>
            {(allEmployees || [])
              // Externals can never be managers · they're not part of the
              // reporting hierarchy. Self-reference also blocked.
              .filter(m => m.email && m.id !== emp.id && !m.is_external)
              .map(m => (
                <option key={m.id} value={m.email}>{m.full_name}</option>
              ))}
            {/* Stale-value safety: when manager_email no longer matches any
                active employee (renamed, deleted), render the raw value so
                the admin can see it instead of a silent blank. */}
            {emp.manager_email
              && !(allEmployees || []).some(m => (m.email || '').toLowerCase() === emp.manager_email.toLowerCase())
              && (
                <option value={emp.manager_email}>
                  {emp.manager_email} {t('admin.manager.orphan')}
                </option>
              )}
          </select>
        </td>
        <td className="px-3 py-2">
          <LastSeenCell t={t} act={activity} />
        </td>
        <td className="px-3 py-2">
          <select
            value={emp.preferred_locale || ''}
            onChange={(e) => onPatch(emp.id, { preferred_locale: e.target.value })}
            className="text-[12.5px] py-1 w-full"
            aria-label={t('admin.localeCol')}
          >
            <option value="">{t('admin.localeAuto')}</option>
            <option value="fr">FR</option>
            <option value="nl">NL</option>
            <option value="en">EN</option>
          </select>
        </td>
        <td className="px-2 py-2 text-center">
          <input
            type="checkbox"
            checked={!!emp.is_admin}
            onChange={(e) => onPatch(emp.id, { is_admin: e.target.checked })}
            aria-label={t('admin.makeAdmin')}
          />
        </td>
        <td className="px-2 py-2 text-right">
          <button
            type="button"
            onClick={() => onActAs(emp)}
            className="p-1 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--accent))] transition"
            title={t('actAs.start')}
            aria-label={t('actAs.start')}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-[hsl(var(--bg-subtle))]">
          <td colSpan={9} className="px-6 py-4">
            <EmployeeStatus emp={emp} onPatch={onPatch} t={t} />
            <div className="mb-4">
              <div className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">
                {t('admin.roles.section')}
              </div>
              <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!emp.is_content_editor}
                  onChange={(e) => onPatch(emp.id, { is_content_editor: e.target.checked })}
                />
                <span style={{ color: 'hsl(var(--text))' }}>{t('admin.roles.contentEditor')}</span>
              </label>
              <p className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1">
                {t('admin.roles.contentEditor.hint')}
              </p>
              <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer mt-3">
                <input
                  type="checkbox"
                  checked={!!emp.is_expense_manager}
                  onChange={(e) => onPatch(emp.id, { is_expense_manager: e.target.checked })}
                />
                <span style={{ color: 'hsl(var(--text))' }}>{t('admin.roles.expenseManager')}</span>
              </label>
              <p className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1">
                {t('admin.roles.expenseManager.hint')}
              </p>
            </div>
            <div className="mb-4">
              <div className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">
                {t('admin.slack.section') || 'Slack'}
              </div>
              <PosteEditor
                value={emp.slack_user_id || ''}
                onCommit={(v) => onPatch(emp.id, { slack_user_id: v })}
                placeholder={t('admin.slack.ph') || 'U02ABCDEF'}
                aria-label="Slack user id"
              />
              <p className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1">
                {t('admin.slack.hint') || 'Slack member ID (Profile → More → Copy member ID).'}
              </p>
            </div>
            <div className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">
              {t('admin.bankAccounts')}
            </div>
            {bankErr && (
              <div className="mb-2 text-[12px] text-[hsl(var(--danger))]">{bankErr}</div>
            )}
            {(emp.bank_accounts || []).length === 0 && (
              <div className="text-[13px] text-[hsl(var(--text-soft))] mb-2">{t('bank.empty')}</div>
            )}
            <ul className="space-y-1.5 mb-3">
              {(emp.bank_accounts || []).map(a => (
                <li key={a.id} className="flex items-center gap-3 px-3 py-2 border border-border rounded-md bg-[hsl(var(--surface))] text-[13px]">
                  <span className="font-num flex-1 truncate">{fmtIban(a.iban)}{a.label ? ` · ${a.label}` : ''}</span>
                  {a.is_preferred
                    ? <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--accent))]"><Star className="h-3 w-3 fill-current" /> {t('bank.preferred')}</span>
                    : <button onClick={() => onUpdateBank(emp.id, a.id, { is_preferred: true })}
                        className="text-[11px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">{t('bank.makePreferred')}</button>}
                  <button onClick={() => onDeleteBank(emp.id, a.id)}
                    className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label={t('common.delete')}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-end gap-2">
              <input className="input !w-56 font-num" placeholder="BE71 0961 2345 6769"
                value={newIban} onChange={e => setNewIban(e.target.value)} />
              <input className="input !w-44" placeholder={t('bank.label.ph')}
                value={newLabel} onChange={e => setNewLabel(e.target.value)} />
              <Button size="sm" onClick={addBank} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('bank.add')}
              </Button>
            </div>
            {onSetAppGrant && (
              <div className="mt-5">
                <div className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2 flex items-center justify-between gap-3">
                  <span>{t('admin.employees.row.apps')}</span>
                  {apps !== null && (
                    <span className="text-[11px] font-num tracking-normal normal-case text-[hsl(var(--text-muted))]">
                      {grantedCount}/{activeApps.length}
                    </span>
                  )}
                </div>
                {apps === null ? (
                  <div className="text-[12.5px] text-[hsl(var(--text-muted))]">{t('common.loading')}</div>
                ) : activeApps.length === 0 ? (
                  <div className="text-[12.5px] text-[hsl(var(--text-muted))]">{t('admin.apps.empty')}</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                    {activeApps.map(a => {
                      const granted = (a.allowed_employees || []).includes(emp.id);
                      return (
                        <label key={a.id}
                          className="flex items-center gap-2 px-2 py-1 rounded-md text-[12.5px] cursor-pointer hover:bg-[hsl(var(--surface-2))]">
                          <input type="checkbox" checked={granted}
                                 onChange={() => onSetAppGrant(emp.id, a.id, !granted)} />
                          {a.logo
                            ? <img src={a.logo} alt="" className="h-3.5 w-3.5 object-contain shrink-0" />
                            : <span className="h-2 w-2 rounded-full shrink-0" style={{ background: a.dot_color || '#888' }} />}
                          <span className="truncate font-medium">{a.name}</span>
                          <span className="ml-auto text-[10.5px] text-[hsl(var(--text-muted))] shrink-0">{a.group}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

/* --------------------------------- Departments --------------------------- */

const DeptRow = ({ d, t, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [fr, setFr] = useState(d.name_fr || '');
  const [nl, setNl] = useState(d.name_nl || '');
  const [en, setEn] = useState(d.name_en || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const start = () => {
    setFr(d.name_fr || ''); setNl(d.name_nl || ''); setEn(d.name_en || '');
    setEditing(true); setError('');
  };
  const cancel = () => { setEditing(false); setError(''); };
  const save = async () => {
    if (!fr.trim()) { setError(t('admin.dept.frRequired')); return; }
    setBusy(true); setError('');
    try {
      await onSave(d.id, { name_fr: fr.trim(), name_nl: nl.trim(), name_en: en.trim() });
      setEditing(false);
    } catch (e) {
      setError(e.response?.data?.detail || t('admin.saveFailed'));
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!window.confirm(t('admin.dept.confirmDelete', { name: d.name_fr }))) return;
    setBusy(true); setError('');
    try {
      await onDelete(d.id);
    } catch (e) {
      setError(e.response?.data?.detail || t('admin.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <tr className="border-t border-[hsl(var(--rule))] bg-[hsl(var(--bg-subtle))]">
        <td className="px-3 py-2"><input className="input !w-full" value={fr} onChange={e => setFr(e.target.value)} /></td>
        <td className="px-3 py-2"><input className="input !w-full" value={nl} onChange={e => setNl(e.target.value)} /></td>
        <td className="px-3 py-2"><input className="input !w-full" value={en} onChange={e => setEn(e.target.value)} /></td>
        <td className="px-3 py-2 text-right">
          {error && <span className="text-[11.5px] text-[hsl(var(--danger))] mr-2">{error}</span>}
          <button onClick={cancel} disabled={busy}
            className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] mr-2" aria-label={t('common.cancel')}>
            <X className="h-4 w-4" />
          </button>
          <button onClick={save} disabled={busy}
            className="text-[hsl(var(--accent))] hover:opacity-80" aria-label={t('common.save')}>
            <Check className="h-4 w-4" />
          </button>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-t border-[hsl(var(--rule))] hover:bg-[hsl(var(--surface-2))]">
      <td className="px-4 py-2.5 font-medium">{d.name_fr}</td>
      <td className="px-4 py-2.5 text-[hsl(var(--text-soft))]">{d.name_nl || '·'}</td>
      <td className="px-4 py-2.5 text-[hsl(var(--text-soft))]">{d.name_en || '·'}</td>
      <td className="px-3 py-2.5 text-right">
        {error && <span className="text-[11.5px] text-[hsl(var(--danger))] mr-2">{error}</span>}
        <button onClick={start} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] mr-2" aria-label={t('common.edit') || 'Edit'}>
          {t('admin.dept.rename')}
        </button>
        <button onClick={del} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label={t('common.delete')}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
};

const DepartmentsTab = ({ t, departments }) => {
  const [adding, setAdding] = useState(false);
  const [fr, setFr] = useState('');
  const [nl, setNl] = useState('');
  const [en, setEn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = async () => {
    const list = await api.departments();
    setDepartmentCatalogue(list);
  };

  const create = async () => {
    if (!fr.trim()) { setError(t('admin.dept.frRequired')); return; }
    setBusy(true); setError('');
    try {
      await api.adminCreateDepartment({ name_fr: fr.trim(), name_nl: nl.trim(), name_en: en.trim() });
      await refresh();
      setFr(''); setNl(''); setEn(''); setAdding(false);
    } catch (e) {
      setError(e.response?.data?.detail || t('admin.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const save = async (id, payload) => {
    await api.adminUpdateDepartment(id, payload);
    await refresh();
  };
  const del = async (id) => {
    await api.adminDeleteDepartment(id);
    await refresh();
  };

  return (
    <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
      <table className="w-full text-[14px]">
        <thead className="bg-[hsl(var(--bg-subtle))] text-[13px] text-[hsl(var(--text-muted))] border-b border-border">
          <tr>
            <th className="text-left font-medium px-4 py-3">{t('admin.dept.fr')}</th>
            <th className="text-left font-medium px-4 py-3">{t('admin.dept.nl')}</th>
            <th className="text-left font-medium px-4 py-3">{t('admin.dept.en')}</th>
            <th className="w-[180px]" />
          </tr>
        </thead>
        <tbody>
          {departments.map(d => (
            <DeptRow key={d.id} d={d} t={t} onSave={save} onDelete={del} />
          ))}
          {adding ? (
            <tr className="border-t border-[hsl(var(--rule))] bg-[hsl(var(--bg-subtle))]">
              <td className="px-3 py-2"><input className="input !w-full" placeholder={t('admin.dept.fr')} value={fr} onChange={e => setFr(e.target.value)} autoFocus /></td>
              <td className="px-3 py-2"><input className="input !w-full" placeholder={t('admin.dept.nl')} value={nl} onChange={e => setNl(e.target.value)} /></td>
              <td className="px-3 py-2"><input className="input !w-full" placeholder={t('admin.dept.en')} value={en} onChange={e => setEn(e.target.value)} /></td>
              <td className="px-3 py-2 text-right">
                {error && <span className="text-[11.5px] text-[hsl(var(--danger))] mr-2">{error}</span>}
                <button onClick={() => { setAdding(false); setError(''); }} disabled={busy}
                  className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] mr-2" aria-label={t('common.cancel')}>
                  <X className="h-4 w-4" />
                </button>
                <button onClick={create} disabled={busy}
                  className="text-[hsl(var(--accent))] hover:opacity-80" aria-label={t('common.save')}>
                  <Check className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ) : (
            <tr className="border-t border-[hsl(var(--rule))]">
              <td colSpan={4} className="px-4 py-2.5">
                <button onClick={() => setAdding(true)}
                  className="inline-flex items-center gap-1.5 text-[13px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">
                  <Plus className="h-3.5 w-3.5" /> {t('admin.dept.add')}
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

/* ---------------------------------- Posts -------------------------------- */

const CAT_TONE = {
  hr:       'badge--info',
  event:    'badge--ok',
  reminder: 'badge--bad',
  news:     'badge--neutral',
};
const CAT_ORDER = ['', 'hr', 'news', 'event', 'reminder'];

/* Filter strip for the expense-reports table · status pills (controlled
 * by the parent because the fetch keys off the server-side status query),
 * plus client-side ref/name search, department picker, date range and
 * a min-amount cutoff. Active count shown to the right so admins know
 * how aggressive their filter is. */
const ExpenseFilters = ({
  t,
  statusFilter, setStatusFilter,
  q, setQ,
  deptFilter, setDeptFilter,
  fromDate, setFromDate,
  toDate, setToDate,
  minAmount, setMinAmount,
  departments,
  locale,
  count, total,
}) => {
  const hasActiveFilter = q || deptFilter || fromDate || toDate || minAmount;
  const clearAll = () => {
    setQ(''); setDeptFilter('');
    setFromDate(''); setToDate('');
    setMinAmount('');
  };
  return (
    <div className="mb-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {['', 'draft', 'submitted', 'approved', 'rejected'].map(s => (
          <button key={s || 'all'} type="button" onClick={() => setStatusFilter(s)}
            className={`inline-flex items-center rounded-full border px-3 py-1.5 text-[13px] transition ${
              statusFilter === s
                ? 'border-[hsl(var(--text))] bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
                : 'border-border bg-[hsl(var(--surface))] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text))]'}`}>
            {s ? t(`status.${s}`) : t('common.all')}
          </button>
        ))}
        <span className="ml-auto text-[12px] text-[hsl(var(--text-muted))] font-num">
          {hasActiveFilter ? `${count} / ${total}` : `${total}`}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('admin.expenses.searchPh') || 'Réf, nom, email…'}
            className="input !pl-9 !w-60 text-[13px]"
          />
        </label>
        <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
                className="input !w-auto text-[13px]">
          <option value="">{t('admin.expenses.allDepts') || 'Tous les départements'}</option>
          {(departments || []).map(d => (
            <option key={d.code} value={d.code}>{deptLabel(d.code, locale)}</option>
          ))}
        </select>
        <div className="inline-flex items-center gap-1 text-[12px] text-[hsl(var(--text-muted))]">
          <span>{t('admin.expenses.from') || 'Du'}</span>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                 className="input !w-auto text-[13px] font-num" />
          <span>{t('admin.expenses.to') || 'au'}</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                 className="input !w-auto text-[13px] font-num" />
        </div>
        <div className="inline-flex items-center gap-1 text-[12px] text-[hsl(var(--text-muted))]">
          <span>{t('admin.expenses.minAmount') || 'Min €'}</span>
          <input type="number" min="0" step="10" value={minAmount}
                 onChange={e => setMinAmount(e.target.value)}
                 placeholder="0"
                 className="input !w-24 text-[13px] font-num" />
        </div>
        {hasActiveFilter && (
          <button type="button" onClick={clearAll}
                  className="text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] underline">
            {t('common.clear') || 'Effacer'}
          </button>
        )}
      </div>
    </div>
  );
};

const PostsTab = ({ t, intlLocale }) => {
  const [posts, setPosts] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fmtDateTime = (iso) => iso
    ? new Intl.DateTimeFormat(intlLocale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))
    : '·';

  const load = async (cat) => {
    try {
      const list = await api.announcements(cat ? { category: cat } : undefined);
      setPosts(list);
    } catch {
      setPosts([]);
    }
  };

  useEffect(() => { load(categoryFilter); }, [categoryFilter]);

  const sortPosts = (arr) => arr.slice().sort((a, b) =>
    Number(b.pinned) - Number(a.pinned) || new Date(b.published_at) - new Date(a.published_at));

  // Local search + date-range filter on top of the category fetch.
  // Search hits title, body and author (across all stored locales) so
  // an admin can find an NL post by its French keyword too.
  const filteredPosts = useMemo(() => {
    if (!posts) return null;
    const needle = q.trim().toLowerCase();
    const fromTs = fromDate ? new Date(fromDate).getTime() : null;
    const toTs   = toDate   ? new Date(toDate).getTime() + 86400000 : null;
    return posts.filter(p => {
      if (fromTs != null && new Date(p.published_at).getTime() < fromTs) return false;
      if (toTs   != null && new Date(p.published_at).getTime() >= toTs)  return false;
      if (!needle) return true;
      const tr = p.translations || {};
      const haystack = [
        p.title, p.body, p.author_full_name, p.author_email,
        tr.fr?.title, tr.fr?.body, tr.nl?.title, tr.nl?.body, tr.en?.title, tr.en?.body,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [posts, q, fromDate, toDate]);

  const create = async (payload, files = []) => {
    try {
      const created = await api.createAnnouncement(payload);
      for (const f of files) {
        await api.uploadAnnouncementAttachment(created.id, f);
      }
      if (files.length) {
        await load(categoryFilter);
      } else if (!categoryFilter || created.category === categoryFilter) {
        setPosts(p => sortPosts([created, ...(p || [])]));
      }
    } catch (e) {
      setError(e.response?.data?.detail || t('admin.saveFailed'));
      throw e;
    }
  };

  const update = async (payload) => {
    if (!editing) return;
    try {
      const updated = await api.updateAnnouncement(editing.id, payload);
      // If the category changed and we're filtering, the post may leave the list.
      if (categoryFilter && updated.category !== categoryFilter) {
        setPosts(p => (p || []).filter(x => x.id !== editing.id));
      } else {
        setPosts(p => sortPosts((p || []).map(x => x.id === editing.id
          ? { ...updated, attachments: x.attachments, comments: x.comments, reactions: x.reactions }
          : x)));
      }
    } catch (e) {
      setError(e.response?.data?.detail || t('admin.saveFailed'));
      throw e;
    }
  };

  const togglePin = async (post) => {
    try {
      const updated = await api.updateAnnouncement(post.id, { pinned: !post.pinned });
      setPosts(p => sortPosts((p || []).map(x => x.id === post.id
        ? { ...updated, attachments: x.attachments, comments: x.comments, reactions: x.reactions }
        : x)));
    } catch (e) {
      setError(e.response?.data?.detail || t('admin.saveFailed'));
    }
  };

  const remove = async (post) => {
    if (!window.confirm(t('admin.posts.confirmDelete'))) return;
    try {
      await api.deleteAnnouncement(post.id);
      setPosts(p => (p || []).filter(x => x.id !== post.id));
    } catch (e) {
      setError(e.response?.data?.detail || t('admin.saveFailed'));
    }
  };

  return (
    <>
      {error && (
        <div className="mb-4 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
          {error}
        </div>
      )}

      {/* Row 1 · category pills · the "new" CTA stays here so the
          primary action is always visible regardless of search state. */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {CAT_ORDER.map(c => (
          <button key={c || 'all'} type="button" onClick={() => setCategoryFilter(c)}
            className={`inline-flex items-center rounded-full border px-3 py-1.5 text-[13px] transition ${
              categoryFilter === c
                ? 'border-[hsl(var(--text))] bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
                : 'border-border bg-[hsl(var(--surface))] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text))]'}`}>
            {c ? t(`category.${c}`) : t('common.all')}
          </button>
        ))}
        {!composing && !editing && (
          <Button size="sm" onClick={() => setComposing(true)} className="ml-auto gap-1.5">
            <Plus className="h-3.5 w-3.5" /> {t('admin.posts.new')}
          </Button>
        )}
      </div>
      {/* Row 2 · search + date range, separated so the date inputs
          (which render wide because of the placeholder format) get the
          space they need without crowding the pills. */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <label className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('admin.posts.searchPh') || 'Rechercher…'}
            className="input !pl-9 !w-64 text-[13px]"
          />
        </label>
        <div className="inline-flex items-center gap-1.5 text-[12px] text-[hsl(var(--text-muted))]">
          <span className="shrink-0">{t('admin.posts.from') || 'Du'}</span>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                 className="input !w-auto text-[13px] font-num" />
          <span className="shrink-0">{t('admin.posts.to') || 'au'}</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                 className="input !w-auto text-[13px] font-num" />
        </div>
        {(q || fromDate || toDate) && (
          <button type="button" onClick={() => { setQ(''); setFromDate(''); setToDate(''); }}
                  className="text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] underline">
            {t('common.clear') || 'Effacer'}
          </button>
        )}
      </div>

      {composing && (
        <div className="mb-4">
          <AnnouncementComposer
            open
            onClose={() => setComposing(false)}
            onPublish={async (payload, files) => {
              await create(payload, files);
              setComposing(false);
            }}
            defaultCategory={categoryFilter || 'news'}
          />
        </div>
      )}

      {editing && (
        <div className="mb-4">
          <AnnouncementComposer
            open
            initialPost={editing}
            onClose={() => setEditing(null)}
            onPublish={async (payload) => {
              await update(payload);
              setEditing(null);
            }}
          />
        </div>
      )}

      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
        <table className="w-full text-[14px]">
          <thead className="bg-[hsl(var(--bg-subtle))] text-[13px] text-[hsl(var(--text-muted))] border-b border-border">
            <tr>
              <th className="w-8" />
              <th className="text-left font-medium px-4 py-3">{t('admin.posts.colTitle')}</th>
              <th className="text-left font-medium px-4 py-3">{t('admin.posts.colCategory')}</th>
              <th className="text-left font-medium px-4 py-3">{t('admin.posts.colAuthor')}</th>
              <th className="text-left font-medium px-4 py-3">{t('admin.posts.colPublished')}</th>
              <th className="w-28" />
            </tr>
          </thead>
          <tbody>
            {posts === null && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.loading')}</td></tr>
            )}
            {posts !== null && (filteredPosts || []).length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">
                {(q || fromDate || toDate) ? (t('common.empty')) : t('admin.posts.empty')}
              </td></tr>
            )}
            {(filteredPosts || []).map(p => (
              <tr key={p.id} className="border-t border-[hsl(var(--rule))] hover:bg-[hsl(var(--surface-2))]">
                <td className="px-3 py-2.5 text-center">
                  <button type="button" onClick={() => togglePin(p)}
                          className={`transition ${p.pinned ? 'text-[hsl(var(--accent))]' : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]'}`}
                          title={p.pinned ? t('admin.posts.unpin') : t('admin.posts.pin')}
                          aria-label={p.pinned ? t('admin.posts.unpin') : t('admin.posts.pin')}>
                    <Pin className="h-3.5 w-3.5" />
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <button type="button" onClick={() => setEditing(p)}
                          className="text-left font-medium hover:underline truncate">
                    {p.title || t('splash.news.untitled')}
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`badge ${CAT_TONE[p.category] || CAT_TONE.news}`}>
                    {t(`category.${p.category}`)}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-[13px] text-[hsl(var(--text-soft))] truncate">
                  {p.author_full_name || p.author_email}
                </td>
                <td className="px-4 py-2.5 text-[13px] text-[hsl(var(--text-soft))] font-num">
                  {fmtDateTime(p.published_at)}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button type="button" onClick={() => setEditing(p)}
                            className="p-1 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                            title={t('common.edit')} aria-label={t('common.edit')}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={() => remove(p)}
                            className="p-1 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]"
                            title={t('common.delete')} aria-label={t('common.delete')}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

/* ---------------------------------- Page --------------------------------- */

/* Sidebar nav config · grouped sections of admin tabs. Each item's `id`
 * matches the value of the `tab` state machine, so existing routing
 * keeps working · this only swaps the chrome. Add a new tab by adding
 * to `items` here and a `{tab === '...' && <X/>}` block below; group it
 * by dropping it into the right section. */
const ADMIN_SECTIONS = [
  { key: 'hr',         items: [
    { id: 'employees',   iconKey: 'Users' },
    { id: 'departments', iconKey: 'Building2' },
  ]},
  { key: 'accounting', items: [
    { id: 'expenses',    iconKey: 'Receipt' },
  ]},
  { key: 'security',   items: [
    { id: 'apps',             iconKey: 'LayoutGrid' },
    { id: 'access-requests',  iconKey: 'KeyRound' },
  ]},
  { key: 'news',       items: [
    { id: 'posts',        iconKey: 'Newspaper' },
    { id: 'newsletters',  iconKey: 'Mail',         to: '/admin/newsletters' },
    { id: 'events',       iconKey: 'CalendarDays', to: '/admin/newsletter-events' },
    { id: 'polaroids',    iconKey: 'Camera',       to: '/admin/newsletter-photos' },
    { id: 'jobs',         iconKey: 'Briefcase',    to: '/admin/jobs' },
  ]},
];

const ADMIN_NAV_ICONS = { Users, Building2, Receipt, LayoutGrid, Newspaper, KeyRound, Mail, Camera, CalendarDays, Briefcase };

/* A filter belongs in the query string for the same reason a panel belongs in
 * the path: "the approved notes for Commercial" is a view worth sending to
 * someone, and a refresh shouldn't quietly throw away what you set.
 *
 * `replace` because typing in a search box shouldn't bury the back button, and
 * a value equal to the default is dropped so the common case stays a clean URL. */
const useQueryState = (key, fallback = '') => {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? fallback;
  const set = useCallback((next) => {
    setParams(prev => {
      const p = new URLSearchParams(prev);
      if (next == null || next === '' || next === fallback) p.delete(key);
      else p.set(key, next);
      return p;
    }, { replace: true });
  }, [key, fallback, setParams]);
  return [value, set];
};

/* Group ids are stored lowercase (`commerce`, `care`) because they're keys.
 * Nobody wants to read a key, so resolve one to its label and fall back to the
 * raw id when a new group lands before its translation does. */
const groupLabel = (t, group) => {
  if (!group) return '';
  const label = t(`admin.apps.group.${group}`);
  return label === `admin.apps.group.${group}` ? group : label;
};

/* Tabs rendered by this page (the rest of the sidebar links out to its own
 * route). Each one is addressable as /admin/<id> · the URL is the state, so a
 * link, a refresh, the back button and a pasted address all agree. */
const IN_PAGE_TABS = ADMIN_SECTIONS
  .flatMap(sec => sec.items)
  .filter(item => !item.to && !item.href)
  .map(item => item.id);
const DEFAULT_TAB = 'employees';

const Admin = () => {
  const { count: reviewCount } = useReviewQueue();
  const t = useT();
  const { locale, intlLocale } = useLocale();
  const fmtDate = (iso) => iso ? new Intl.DateTimeFormat(intlLocale).format(new Date(iso)) : '·';

  const { tab: tabParam, item, action } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tab = IN_PAGE_TABS.includes(tabParam) ? tabParam : DEFAULT_TAB;
  // `replace` throughout · switching panel isn't a navigation you'd want to
  // step back through one by one.
  const setTab = useCallback(
    (id) => navigate(`/admin/${id}`, { replace: true }),
    [navigate],
  );
  // Canonicalise the address bar: bare /admin, an unknown tab, or the legacy
  // `#access-requests` anchor the access-request emails still send all land on
  // the URL that matches what's actually on screen.
  useEffect(() => {
    const fromHash = (location.hash || '').replace(/^#/, '');
    if (!tabParam && IN_PAGE_TABS.includes(fromHash)) {
      navigate(`/admin/${fromHash}`, { replace: true });
      return;
    }
    if (tabParam !== tab) navigate(`/admin/${tab}`, { replace: true });
  }, [tabParam, tab, location.hash, navigate]);
  const { departments } = useDepartments();
  const [employees, setEmployees] = useState(null);
  // Catalogue of apps lifted to the page so the expanded employee row
  // can show + edit per-employee access. AppsTab still owns its own
  // copy for the matrix / list views, but they refresh independently
  // (cheap, both endpoints are paginatable-but-tiny). Loaded lazily
  // the first time an employee row is expanded.
  const [adminApps, setAdminApps] = useState(null);
  const ensureAdminApps = useCallback(() => {
    if (adminApps !== null) return;
    api.adminApps().then(setAdminApps).catch(() => setAdminApps([]));
  }, [adminApps]);
  /* Toggle one app grant for an employee. Optimistic update on the
   * local adminApps list so the checkbox flips instantly · rolls back
   * on API failure. */
  const setEmployeeAppGrant = useCallback(async (empId, appId, grant) => {
    const app = (adminApps || []).find(a => a.id === appId);
    if (!app) return;
    const next = new Set(app.allowed_employees || []);
    if (grant) next.add(empId); else next.delete(empId);
    const nextList = Array.from(next);
    setAdminApps(arr => (arr || []).map(a => a.id === appId ? { ...a, allowed_employees: nextList } : a));
    try {
      const updated = await api.adminUpdateApp(appId, { allowed_employees: nextList });
      setAdminApps(arr => (arr || []).map(a => a.id === appId ? updated : a));
    } catch (e) {
      setAdminApps(arr => (arr || []).map(a => a.id === appId ? app : a));
      setError(e.response?.data?.detail || e.message);
    }
  }, [adminApps]);
  const [reports, setReports] = useState(null);
  const [statusFilter, setStatusFilter] = useQueryState('status');
  const [expenseQ, setExpenseQ] = useQueryState('q');
  const [expenseDeptFilter, setExpenseDeptFilter] = useQueryState('dept');
  const [expenseFromDate, setExpenseFromDate] = useQueryState('from');
  const [expenseToDate, setExpenseToDate] = useQueryState('to');
  const [expenseMinAmount, setExpenseMinAmount] = useQueryState('min');
  const [empQ, setEmpQ] = useState('');
  const [empDeptFilter, setEmpDeptFilter] = useState('');
  // 'internal' (default · staff) | 'external' (third parties) | 'all'.
  // Externals are tracked in this same table but hidden from the splash
  // team, org chart and manager dropdowns · admins manage them here.
  const [empMembership, setEmpMembership] = useState('internal');
  // Portal-visit summary keyed by lowercased email · feeds the "Last seen"
  // column and the never/inactive filter. Loaded once with the employees.
  const [activity, setActivity] = useState({});
  const [empActivityFilter, setEmpActivityFilter] = useState('');  // '' | 'never' | 'stale'
  // People who left are hidden by default · a toggle brings them back.
  const [empShowInactive, setEmpShowInactive] = useState(false);
  const [addingEmp, setAddingEmp] = useState(false);
  // `addType` mirrors the form's Intern / Extern toggle. Initialised
  // from the current membership filter so opening the form from an
  // "Extern"-filtered view defaults to creating an external · feels
  // like the obvious next step from the surface the admin is on.
  const emptyAddDraft = () => ({
    email: '', full_name: '', poste: '',
    departement: '', manager_email: '', preferred_locale: '',
    is_external: false,
    app_ids: new Set(),  // initial app grants for the new employee
  });
  const [addEmpDraft, setAddEmpDraft] = useState(emptyAddDraft());
  const [addEmpErr, setAddEmpErr] = useState('');
  // Lazy-load the apps catalogue when the form opens · feeds the
  // "initial access" section. Cached after first load so reopening
  // the form doesn't hit the API again.
  const [allApps, setAllApps] = useState(null);
  const openAdd = () => {
    setAddingEmp(true);
    setAddEmpErr('');
    setAddEmpDraft({ ...emptyAddDraft(), is_external: empMembership === 'external' });
    if (allApps === null) {
      api.adminApps().then(setAllApps).catch(() => setAllApps([]));
    }
  };
  // Modules (a restricted page *inside* an app) are granted from the Apps
  // tab where they sit under their parent · this list stays real apps, or
  // the form would ask "give this newcomer Encodage?" out of any context.
  const addableApps = useMemo(
    () => (allApps || []).filter(a => a.is_active && !a.parent),
    [allApps],
  );
  const toggleAddApp = (id) => setAddEmpDraft(d => {
    const next = new Set(d.app_ids);
    if (next.has(id)) next.delete(id); else next.add(id);
    return { ...d, app_ids: next };
  });
  const setAllAddApps = (on) => setAddEmpDraft(d => ({
    ...d,
    app_ids: new Set(on ? addableApps.map(a => a.id) : []),
  }));
  const [error, setError] = useState('');

  // Role flags · gate which Beheer surfaces this user sees. Full admins get
  // everything; an expense manager (accounting) gets only the Onkostennota's
  // tab. Admin-only fetches below wait on this so a limited user never
  // triggers a 403 they'd see as an error.
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.adminMe().then(setMe).catch(() => setMe({ is_admin: false, is_expense_manager: false }));
  }, []);
  const isAdmin = !!me?.is_admin;
  const isExpenseManager = !!me?.is_expense_manager;
  const allowedTabs = useMemo(() => {
    if (isAdmin) return null;              // null = every tab
    const s = new Set();
    if (isExpenseManager) s.add('expenses');
    return s;
  }, [isAdmin, isExpenseManager]);
  const visibleSections = useMemo(() => {
    if (!allowedTabs) return ADMIN_SECTIONS;
    return ADMIN_SECTIONS
      .map(sec => ({ ...sec, items: sec.items.filter(it => allowedTabs.has(it.id)) }))
      .filter(sec => sec.items.length);
  }, [allowedTabs]);
  // Bounce a limited user off any tab they can't open (e.g. the default
  // 'employees') onto their first allowed one.
  useEffect(() => {
    if (allowedTabs && !allowedTabs.has(tab)) {
      const first = visibleSections[0]?.items[0]?.id;
      if (first && first !== tab) setTab(first);
    }
  }, [allowedTabs, visibleSections, tab]);
  // Someone who reached /admin directly with no admin surface at all.
  useEffect(() => {
    if (me && !me.is_admin && !me.is_expense_manager) setError(t('admin.forbidden'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    if (!me?.is_admin) return;   // the employee list + visit summary are admin-only
    api.adminEmployees().then(setEmployees).catch(() => { setEmployees([]); setError(t('admin.forbidden')); });
    // Portal-visit summary · non-blocking; the table renders without it and
    // the "Last seen" column fills in when it arrives.
    api.employeeActivity().then(d => setActivity(d.activity || {})).catch(() => setActivity({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);
  const activityFor = (email) => activity[(email || '').trim().toLowerCase()] || null;

  useEffect(() => {
    if (tab !== 'expenses') return;
    api.adminExpenseReports(statusFilter).then(setReports).catch(() => setReports([]));
  }, [tab, statusFilter]);

  // Client-side narrowing layered on top of the status fetch · admins
  // routinely scan dozens of reports per month, so date / submitter /
  // amount filters cut down to "this week" or "Robbie's notes" without
  // round-tripping the server. Search hits ref, name, email and dept.
  const expenseFilteredReports = useMemo(() => {
    if (!reports) return null;
    const needle = expenseQ.trim().toLowerCase();
    const fromTs = expenseFromDate ? new Date(expenseFromDate).getTime() : null;
    const toTs   = expenseToDate   ? new Date(expenseToDate).getTime() + 86400000 : null;
    const minAmt = expenseMinAmount ? Number(expenseMinAmount) : null;
    return reports.filter(r => {
      if (expenseDeptFilter && r.submitter_departement !== expenseDeptFilter) return false;
      // Date window applies against the relevant date for the status:
      // submitted_at for submitted/approved/rejected, created_at for drafts.
      const refDate = r.submitted_at || r.created_at;
      const ts = refDate ? new Date(refDate).getTime() : null;
      if (fromTs != null && (ts == null || ts < fromTs)) return false;
      if (toTs   != null && (ts == null || ts >= toTs))  return false;
      if (minAmt != null && Number(r.total_tvac || 0) < minAmt) return false;
      if (!needle) return true;
      const ref = `NDF-${String(r.id).padStart(5, '0')}`;
      const haystack = [
        ref, r.submitter_full_name, r.submitter_email, r.title,
        r.submitter_departement, r.submitter_manager_email,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [reports, expenseQ, expenseDeptFilter, expenseFromDate, expenseToDate, expenseMinAmount]);

  const patchEmployee = async (id, payload) => {
    try {
      const updated = await api.adminUpdateEmployee(id, payload);
      setEmployees(arr => arr.map(e => e.id === id ? { ...e, ...updated } : e));
    } catch (e) {
      setError(e.response?.data?.detail || t('admin.saveFailed'));
    }
  };
  const addBank = async (id, payload) => {
    const created = await api.adminAddBankAccount(id, payload);
    setEmployees(arr => arr.map(e => e.id === id
      ? { ...e, bank_accounts: [...(e.bank_accounts || []).map(b => created.is_preferred ? { ...b, is_preferred: false } : b), created] }
      : e));
  };
  const updateBank = async (id, accId, payload) => {
    const updated = await api.adminUpdateBankAccount(id, accId, payload);
    setEmployees(arr => arr.map(e => e.id === id
      ? { ...e, bank_accounts: (e.bank_accounts || []).map(b =>
          b.id === accId ? updated : (updated.is_preferred ? { ...b, is_preferred: false } : b)) }
      : e));
  };
  const deleteBank = async (id, accId) => {
    if (!window.confirm(t('admin.confirmDeleteBank'))) return;
    await api.adminDeleteBankAccount(id, accId);
    setEmployees(arr => arr.map(e => e.id === id
      ? { ...e, bank_accounts: (e.bank_accounts || []).filter(b => b.id !== accId) }
      : e));
  };

  const sortedEmployees = useMemo(
    () => (employees || []).slice().sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')),
    [employees],
  );

  // Client-side filter for the admin employees table · search hits
  // name, email, role (poste), department label and manager. Department
  // picker narrows further. The unfiltered `sortedEmployees` is still
  // passed as `allEmployees` to each row so the manager dropdown can
  // resolve names that aren't visible in the current view.
  const filteredEmployees = useMemo(() => {
    const needle = empQ.trim().toLowerCase();
    return sortedEmployees.filter(emp => {
      if (!empShowInactive && !emp.is_active) return false;
      if (empMembership === 'internal' && emp.is_external) return false;
      if (empMembership === 'external' && !emp.is_external) return false;
      if (empDeptFilter && emp.departement !== empDeptFilter) return false;
      if (empActivityFilter) {
        const act = activity[(emp.email || '').trim().toLowerCase()];
        if (empActivityFilter === 'never' && act && act.last_seen) return false;
        if (empActivityFilter === 'stale') {
          // Keep only those not seen in the last 30 days (never-seen included).
          if (act && (act.days_30 || 0) > 0) return false;
        }
      }
      if (!needle) return true;
      const haystack = [
        emp.full_name, emp.email, emp.poste, emp.localisation,
        emp.telephone, emp.manager_email, emp.manager_name,
        emp.departement ? deptLabel(emp.departement, locale) : '',
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [sortedEmployees, empQ, empDeptFilter, empMembership, empActivityFilter, empShowInactive, activity, locale]);
  const inactiveCount = useMemo(() => sortedEmployees.filter(e => !e.is_active).length, [sortedEmployees]);
  const activeCount = sortedEmployees.length - inactiveCount;

  // Group filtered employees by department so the admin can scan
  // "give me everyone in IT" without staring at a flat alphabetical
  // wall. Empty-dept rows bucket at the end so the structured ones
  // dominate. Externals (Extern filter) are usually dept-less and
  // land in the same "Geen afdeling" bucket.
  const groupedFilteredEmployees = useMemo(() => {
    const NO_DEPT = '__none__';
    const labelFor = (k) => k === NO_DEPT ? t('splash.team.noDept') : deptLabel(k, locale);
    const buckets = new Map();
    filteredEmployees.forEach(e => {
      const key = e.departement || NO_DEPT;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(e);
    });
    return Array.from(buckets.entries())
      .sort(([a], [b]) => {
        if (a === NO_DEPT) return 1;
        if (b === NO_DEPT) return -1;
        return labelFor(a).localeCompare(labelFor(b));
      })
      .map(([k, list]) => ({ key: k, label: labelFor(k), employees: list }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEmployees, locale, t]);

  // Lightweight pending-count poll for the access-requests nav badge ·
  // small surface, costs one cheap GET on mount and after returning to
  // the page. Avoids websocket / SSE plumbing for now; admins can
  // refresh by clicking into the tab.
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  useEffect(() => {
    if (!me?.is_admin) return;   // access-requests badge is an admin-only surface
    api.adminAppAccessRequests()
      .then(rows => setPendingRequestCount((rows || []).filter(r => r.status === 'pending').length))
      .catch(() => setPendingRequestCount(0));
  }, [tab, me]);

  const NavLink = ({ id, iconKey, to, href }) => {
    const Icon = ADMIN_NAV_ICONS[iconKey] || LayoutGrid;
    // An in-page panel, as opposed to a sidebar entry that leaves for its
    // own route (newsletters, jobs…).
    const isTab = !to && !href;
    const active = isTab && tab === id;
    const badge = id === 'access-requests' && pendingRequestCount > 0 ? pendingRequestCount : null;
    const baseClass = `w-full text-left inline-flex items-center gap-2 px-2.5 py-1.5 text-[13px] rounded-md transition ${
      active
        ? 'bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
        : 'text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--text))]'
    }`;
    const inner = (
      <>
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate flex-1">{t(`admin.tab.${id}`)}</span>
        {badge != null && (
          <span className="ml-auto text-[10px] font-medium rounded-full px-1.5 py-0.5"
                style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--surface))', minWidth: 18, textAlign: 'center' }}>
            {badge}
          </span>
        )}
      </>
    );
    if (href) {
      return <a href={href} target="_blank" rel="noreferrer" className={baseClass}>{inner}</a>;
    }
    // A real <Link>, so the sidebar can be middle-clicked and its address
    // copied like any other navigation.
    return <Link to={to || `/admin/${id}`} className={baseClass}>{inner}</Link>;
  };

  const { start: startPreview, startActingAs } = useImpersonation();
  const [probe, setProbe] = useState(null);   // null · idle · object · result
  const [probing, setProbing] = useState(false);

  const runProbe = async () => {
    setProbing(true);
    try {
      setProbe(await api.storageProbe());
    } catch (e) {
      setProbe({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setProbing(false);
    }
  };

  return (
    <Shell reviewCount={reviewCount} wide>
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-[1.75rem] flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-[hsl(var(--accent))]" /> {t('admin.title')}
          </h1>
          <p className="text-[15px] text-[hsl(var(--text-soft))] mt-1.5">{t('admin.intro')}</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={runProbe} disabled={probing} className="gap-1.5">
              <Cloud className="h-3.5 w-3.5" /> {probing ? t('admin.probe.running') : t('admin.probe.cta')}
            </Button>
            <Button variant="outline" size="sm" onClick={startPreview} className="gap-1.5">
              <Eye className="h-3.5 w-3.5" /> {t('preview.start')}
            </Button>
          </div>
        )}
      </header>

      {probe && <StorageProbeResult t={t} probe={probe} onClose={() => setProbe(null)} />}

      {error && (
        <div className="mb-4 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-6">
        {/* Sidebar nav · sections grouped by domain (HR, Accounting,
            Security, News). Stays in view on lg+; collapses to top of
            content on smaller screens. */}
        <nav aria-label={t('admin.title')} className="space-y-4">
          {visibleSections.map(section => (
            <div key={section.key}>
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-medium text-[hsl(var(--text-muted))] px-2.5 mb-1.5">
                {t(`admin.section.${section.key}`)}
              </div>
              <div className="space-y-0.5">
                {section.items.map(item => (
                  <NavLink key={item.id} id={item.id} iconKey={item.iconKey} to={item.to} href={item.href} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="min-w-0">
      {tab === 'apps' && <AppsTab t={t} item={item} action={action} />}

      {tab === 'access-requests' && <AccessRequestsTab t={t} />}

      {tab === 'employees' && isAdmin && (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="inline-flex rounded-md border border-border overflow-hidden text-[12.5px]">
              {['internal', 'external', 'all'].map(opt => (
                <button key={opt} type="button" onClick={() => setEmpMembership(opt)}
                  className={`px-2.5 py-1.5 transition ${empMembership === opt
                    ? 'bg-[hsl(var(--text))] text-[hsl(var(--surface))]'
                    : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]'}`}>
                  {t(`admin.employees.membership.${opt}`)}
                </button>
              ))}
            </div>
            <label className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
              <input
                value={empQ} onChange={e => setEmpQ(e.target.value)}
                placeholder={t('admin.employees.searchPh') || 'Nom, email, fonction…'}
                className="input !pl-9 !w-64 text-[13px]"
              />
            </label>
            <select value={empDeptFilter} onChange={e => setEmpDeptFilter(e.target.value)}
                    className="input !w-auto text-[13px]">
              <option value="">{t('admin.expenses.allDepts') || 'Tous les départements'}</option>
              {(departments || []).map(d => (
                <option key={d.code} value={d.code}>{deptLabel(d.code, locale)}</option>
              ))}
            </select>
            <select value={empActivityFilter} onChange={e => setEmpActivityFilter(e.target.value)}
                    className="input !w-auto text-[13px]"
                    title={t('admin.activity.filterHint')}>
              <option value="">{t('admin.activity.filterAll')}</option>
              <option value="never">{t('admin.activity.filterNever')}</option>
              <option value="stale">{t('admin.activity.filterStale')}</option>
            </select>
            {(empQ || empDeptFilter || empActivityFilter) && (
              <button type="button" onClick={() => { setEmpQ(''); setEmpDeptFilter(''); setEmpActivityFilter(''); }}
                      className="text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] underline">
                {t('common.clear') || 'Effacer'}
              </button>
            )}
            {inactiveCount > 0 && (
              <label className="inline-flex items-center gap-1.5 text-[12.5px] text-[hsl(var(--text-soft))] cursor-pointer">
                <input type="checkbox" checked={empShowInactive} onChange={e => setEmpShowInactive(e.target.checked)} />
                {t('admin.showInactive').replace('{n}', inactiveCount)}
              </label>
            )}
            <span className="ml-auto text-[12px] text-[hsl(var(--text-muted))] font-num">
              {(empQ || empDeptFilter || empActivityFilter)
                ? `${filteredEmployees.length} / ${empShowInactive ? sortedEmployees.length : activeCount}`
                : `${empShowInactive ? sortedEmployees.length : activeCount}`}
            </span>
            {!addingEmp && (
              <Button size="sm" onClick={openAdd} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('admin.employees.add')}
              </Button>
            )}
          </div>

          {addingEmp && (
            <div className="mb-3 border border-border rounded-lg bg-[hsl(var(--bg-subtle))] p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-[hsl(var(--text-soft))]">{t('admin.employees.addType')}</span>
                <div className="inline-flex rounded-md border border-border overflow-hidden text-[12.5px]">
                  <button type="button" onClick={() => setAddEmpDraft(d => ({ ...d, is_external: false }))}
                    className={`px-2.5 py-1 transition ${!addEmpDraft.is_external
                      ? 'bg-[hsl(var(--text))] text-[hsl(var(--surface))]'
                      : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]'}`}>
                    {t('admin.employees.membership.internal')}
                  </button>
                  <button type="button" onClick={() => setAddEmpDraft(d => ({ ...d, is_external: true }))}
                    className={`px-2.5 py-1 transition ${addEmpDraft.is_external
                      ? 'bg-[hsl(var(--text))] text-[hsl(var(--surface))]'
                      : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]'}`}>
                    {t('admin.employees.membership.external')}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <div className="field">
                  <label className="field-label">{t('directory.col.email')} <span className="text-[hsl(var(--danger))]">*</span></label>
                  <input type="email" className="input" autoFocus
                         value={addEmpDraft.email}
                         onChange={e => setAddEmpDraft(d => ({ ...d, email: e.target.value }))}
                         placeholder={addEmpDraft.is_external ? 'contact@partner.be' : 'prenom@lamptwist.be'} />
                </div>
                <div className="field">
                  <label className="field-label">{t('admin.employees.fullName')} <span className="text-[hsl(var(--danger))]">*</span></label>
                  <input type="text" className="input"
                         value={addEmpDraft.full_name}
                         onChange={e => setAddEmpDraft(d => ({ ...d, full_name: e.target.value }))} />
                </div>
                <div className="field">
                  <label className="field-label">{t('directory.col.role')} <span className="text-[hsl(var(--danger))]">*</span></label>
                  <input type="text" className="input"
                         value={addEmpDraft.poste}
                         onChange={e => setAddEmpDraft(d => ({ ...d, poste: e.target.value }))}
                         placeholder={addEmpDraft.is_external ? t('admin.employees.externalPostePh') : t('admin.employees.internalPostePh')} />
                </div>
                <div className="field">
                  <label className="field-label">
                    {t('directory.col.department')}
                    {!addEmpDraft.is_external && <span className="text-[hsl(var(--danger))]"> *</span>}
                    {addEmpDraft.is_external && <span className="text-[hsl(var(--text-muted))] ml-1">{t('common.optional')}</span>}
                  </label>
                  <select className="input"
                          value={addEmpDraft.departement}
                          onChange={e => setAddEmpDraft(d => ({ ...d, departement: e.target.value }))}>
                    <option value="">{t('admin.noDept')}</option>
                    {(departments || []).map(d => (
                      <option key={d.code} value={d.code}>{deptLabel(d.code, locale)}</option>
                    ))}
                  </select>
                </div>
                {!addEmpDraft.is_external && (
                  <div className="field">
                    <label className="field-label">
                      {t('admin.manager')} <span className="text-[hsl(var(--text-muted))]">{t('common.optional')}</span>
                    </label>
                    <select className="input"
                            value={addEmpDraft.manager_email}
                            onChange={e => setAddEmpDraft(d => ({ ...d, manager_email: e.target.value }))}>
                      <option value="">{t('admin.noManager')}</option>
                      {(sortedEmployees || [])
                        .filter(m => m.email && !m.is_external)
                        .map(m => (<option key={m.id} value={m.email}>{m.full_name}</option>))}
                    </select>
                  </div>
                )}
                <div className="field">
                  <label className="field-label">
                    {t('admin.localeCol')} <span className="text-[hsl(var(--text-muted))]">{t('common.optional')}</span>
                  </label>
                  <select className="input"
                          value={addEmpDraft.preferred_locale}
                          onChange={e => setAddEmpDraft(d => ({ ...d, preferred_locale: e.target.value }))}>
                    <option value="">{t('admin.localeAuto')}</option>
                    <option value="fr">Français</option>
                    <option value="nl">Nederlands</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </div>

              {/* Step 2 · pick which apps the new employee gets access to.
                  Default is none (the model has no public mode anymore),
                  so this section spares admins a follow-up trip through
                  the access matrix for the obvious-up-front grants. */}
              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-1.5 gap-3 flex-wrap">
                  <div>
                    <div className="text-[12px] font-medium text-[hsl(var(--text-soft))]">
                      {t('admin.employees.add.access')}
                    </div>
                    <div className="text-[11.5px] text-[hsl(var(--text-muted))]">
                      {t('admin.employees.add.accessHint')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11.5px] text-[hsl(var(--text-muted))]">
                      {t('admin.apps.f.access.countOf', { n: addEmpDraft.app_ids.size, total: addableApps.length })}
                    </span>
                    <button type="button" onClick={() => setAllAddApps(true)}
                      className="text-[11.5px] px-2 py-0.5 rounded-md border border-border hover:bg-[hsl(var(--surface))]">
                      {t('admin.apps.f.access.grantAll')}
                    </button>
                    <button type="button" onClick={() => setAllAddApps(false)}
                      className="text-[11.5px] px-2 py-0.5 rounded-md border border-border hover:bg-[hsl(var(--surface))]">
                      {t('admin.apps.f.access.revokeAll')}
                    </button>
                  </div>
                </div>
                {allApps === null ? (
                  <div className="text-[12px] text-[hsl(var(--text-muted))] py-2">{t('common.loading')}</div>
                ) : addableApps.length === 0 ? (
                  <div className="text-[12px] text-[hsl(var(--text-muted))] py-2">{t('admin.apps.empty')}</div>
                ) : (
                  <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-[hsl(var(--surface))]">
                    <ul className="divide-y divide-[hsl(var(--rule))]">
                      {addableApps.map(a => {
                        const checked = addEmpDraft.app_ids.has(a.id);
                        return (
                          <li key={a.id}>
                            <label className="flex items-center gap-2.5 px-3 py-1.5 text-[13px] cursor-pointer hover:bg-[hsl(var(--bg-subtle))]">
                              <input type="checkbox" checked={checked} onChange={() => toggleAddApp(a.id)} />
                              {a.logo
                                ? <img src={a.logo} alt="" className="h-4 w-4 object-contain shrink-0" />
                                : <span className="h-2 w-2 rounded-full shrink-0" style={{ background: a.dot_color || '#888' }} />}
                              <span className="flex-1 truncate font-medium">{a.name}</span>
                              <span className="text-[11px] text-[hsl(var(--text-muted))] shrink-0">{groupLabel(t, a.group)}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="outline" size="sm"
                        onClick={() => { setAddingEmp(false); setAddEmpDraft(emptyAddDraft()); setAddEmpErr(''); }}>
                  {t('common.cancel')}
                </Button>
                <Button size="sm" onClick={async () => {
                  setAddEmpErr('');
                  const email = addEmpDraft.email.trim();
                  const fullName = addEmpDraft.full_name.trim();
                  const poste = addEmpDraft.poste.trim();
                  const dept = (addEmpDraft.departement || '').trim();
                  if (!email || !fullName || !poste) {
                    setAddEmpErr(t('admin.employees.addRequired'));
                    return;
                  }
                  // Department is only required for internal hires ·
                  // externals (freelancers, partners) often aren't tied
                  // to a LampTwist department.
                  if (!addEmpDraft.is_external && !dept) {
                    setAddEmpErr(t('admin.employees.deptRequired'));
                    return;
                  }
                  try {
                    const created = await api.adminCreateEmployee({
                      email, full_name: fullName, poste,
                      departement: dept,
                      // Manager + locale optional · backend treats empty
                      // strings as "no preference / no manager".
                      manager_email: addEmpDraft.is_external ? '' : (addEmpDraft.manager_email || ''),
                      preferred_locale: addEmpDraft.preferred_locale || '',
                      is_external: addEmpDraft.is_external,
                      initial_app_grants: Array.from(addEmpDraft.app_ids),
                    });
                    setEmployees(arr => [...(arr || []), created]);
                    // Snap the filter to whatever type was just created
                    // so the new row is visible (or stay on "all" if
                    // the admin was already there).
                    if (empMembership !== 'all') {
                      setEmpMembership(addEmpDraft.is_external ? 'external' : 'internal');
                    }
                    setAddingEmp(false);
                    setAddEmpDraft(emptyAddDraft());
                  } catch (e) {
                    const d = e.response?.data;
                    setAddEmpErr(
                      (Array.isArray(d?.email) && d.email[0])
                      || d?.detail
                      || (typeof d === 'string' ? d : JSON.stringify(d || {}))
                    );
                  }
                }} className="gap-1.5">
                  <Check className="h-3.5 w-3.5" /> {t('admin.employees.add.save')}
                </Button>
              </div>
              {addEmpErr && (
                <div className="text-[12px] text-[hsl(var(--danger))]">{addEmpErr}</div>
              )}
            </div>
          )}
          <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
            <table className="w-full text-[13.5px] table-fixed">
              <colgroup>
                <col className="w-[19%]" />{/* Medewerker */}
                <col className="w-[16%]" />{/* E-mail */}
                <col className="w-[13%]" />{/* Afdeling */}
                <col className="w-[11%]" />{/* Functie */}
                <col className="w-[12%]" />{/* Manager */}
                <col className="w-[10%]" />{/* Laatst gezien */}
                <col className="w-[8%]"  />{/* Taal */}
                <col className="w-[6%]"  />{/* Beheerder */}
                <col className="w-[5%]"  />{/* Action */}
              </colgroup>
              <thead className="bg-[hsl(var(--bg-subtle))] text-[12.5px] text-[hsl(var(--text-muted))] border-b border-border">
                <tr>
                  <th className="text-left font-medium px-3 py-2.5">{t('directory.col.collaborator')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('directory.col.email')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('directory.col.department')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('directory.col.role')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('admin.manager')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('admin.activity.col')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('admin.localeCol')}</th>
                  <th className="text-center font-medium px-2 py-2.5">{t('admin.adminCol')}</th>
                  <th className="px-2" />
                </tr>
              </thead>
              <tbody>
                {employees === null && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.loading')}</td></tr>
                )}
                {employees !== null && filteredEmployees.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.empty')}</td></tr>
                )}
                {groupedFilteredEmployees.map(group => (
                  <React.Fragment key={group.key}>
                    <tr>
                      <td colSpan={9} className="px-3 py-1.5 bg-[hsl(var(--bg-subtle))] border-y border-[hsl(var(--rule))]">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10.5px] uppercase tracking-[0.14em] font-medium text-[hsl(var(--text-soft))]">
                            {group.label}
                          </span>
                          <span className="text-[11px] font-num text-[hsl(var(--text-muted))]">{group.employees.length}</span>
                        </div>
                      </td>
                    </tr>
                    {group.employees.map(emp => (
                      <EmployeeRow key={emp.id} emp={emp} locale={locale} t={t}
                        departments={departments}
                        allEmployees={sortedEmployees}
                        onActAs={(e) => {
                          if (window.confirm(t('actAs.confirm', { name: e.full_name || e.email }))) {
                            startActingAs(e.email, e.full_name || e.email);
                          }
                        }}
                        onPatch={patchEmployee} onAddBank={addBank}
                        onUpdateBank={updateBank} onDeleteBank={deleteBank}
                        highlightExternal={empMembership === 'all'}
                        activity={activityFor(emp.email)}
                        apps={adminApps}
                        onLoadApps={ensureAdminApps}
                        onSetAppGrant={setEmployeeAppGrant} />
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'departments' && (
        <DepartmentsTab t={t} departments={departments} />
      )}

      {tab === 'posts' && (
        <PostsTab t={t} intlLocale={intlLocale} />
      )}

      {tab === 'expenses' && (
        <>
          <ExpenseFilters
            t={t}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            q={expenseQ} setQ={setExpenseQ}
            deptFilter={expenseDeptFilter} setDeptFilter={setExpenseDeptFilter}
            fromDate={expenseFromDate} setFromDate={setExpenseFromDate}
            toDate={expenseToDate} setToDate={setExpenseToDate}
            minAmount={expenseMinAmount} setMinAmount={setExpenseMinAmount}
            departments={departments}
            locale={locale}
            count={expenseFilteredReports?.length || 0}
            total={reports?.length || 0}
          />
          <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
            <table className="w-full text-[14px]">
              <thead className="bg-[hsl(var(--bg-subtle))] text-[13px] text-[hsl(var(--text-muted))] border-b border-border">
                <tr>
                  <th className="text-left font-medium px-4 py-3">{t('admin.ref')}</th>
                  <th className="text-left font-medium px-4 py-3">{t('editor.collaborator')}</th>
                  <th className="text-left font-medium px-4 py-3">{t('directory.col.department')}</th>
                  <th className="text-left font-medium px-4 py-3">{t('admin.statusCol')}</th>
                  <th className="text-right font-medium px-4 py-3">{t('editor.col.tvac')}</th>
                  <th className="text-left font-medium px-4 py-3">{t('admin.submitted')}</th>
                  <th className="text-left font-medium px-4 py-3">{t('admin.decided')}</th>
                  <th className="text-left font-medium px-4 py-3">{t('admin.odooCol')}</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {reports === null && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.loading')}</td></tr>
                )}
                {reports !== null && (expenseFilteredReports || []).length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.empty')}</td></tr>
                )}
                {(expenseFilteredReports || []).map(r => (
                  <tr key={r.id} className="border-t border-[hsl(var(--rule))] hover:bg-[hsl(var(--surface-2))]">
                    <td className="px-4 py-2.5 font-num text-[13px]">NDF-{String(r.id).padStart(5, '0')}</td>
                    <td className="px-4 py-2.5 truncate">{r.submitter_full_name || r.submitter_email}</td>
                    <td className="px-4 py-2.5 text-[hsl(var(--text-soft))] truncate">{r.submitter_departement ? deptLabel(r.submitter_departement, locale) : '·'}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2.5 text-right font-num">{fmtMoney(r.total_tvac)}</td>
                    <td className="px-4 py-2.5 text-[13px] text-[hsl(var(--text-soft))] font-num">{fmtDate(r.submitted_at)}</td>
                    <td className="px-4 py-2.5 text-[13px] text-[hsl(var(--text-soft))] font-num">{fmtDate(r.manager_decided_at)}</td>
                    <td className="px-4 py-2.5">
                      {r.status === 'approved' ? <OdooBadge odoo={r.odoo} /> : <span className="text-[hsl(var(--text-muted))]">·</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Link to={`/review/${r.id}`} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]" aria-label={t('common.open') || 'Open'}>
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
        </div>
      </div>
    </Shell>
  );
};

/* Cloud-storage probe · diagnostic for the GCS wiring. Writes a tiny
 * file, signs a URL, reads it back, deletes. Surfaces each step so the
 * operator can see *which* part failed (creds, perms, bucket name…). */
const StorageProbeResult = ({ t, probe, onClose }) => {
  const Icon = probe.ok ? Check : AlertCircle;
  return (
    <div className={`mb-4 rounded-lg border px-4 py-3
        ${probe.ok
          ? 'border-[hsl(var(--success)/.3)] bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]'
          : 'border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))]'}`}>
      <div className="flex items-start gap-3">
        <Icon className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0 text-[13px]">
          <div className="font-medium mb-1">
            {probe.ok ? t('admin.probe.ok') : t('admin.probe.failed')}
            {typeof probe.ms_total === 'number' && (
              <span className="ml-2 text-[11px] opacity-70 font-num">{probe.ms_total} ms</span>
            )}
          </div>
          <div className="text-[12px] grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 mb-2">
            <div><span className="opacity-70">{t('admin.probe.backend')}:</span> <span className="font-num">{probe.backend || '·'}</span></div>
            <div><span className="opacity-70">{t('admin.probe.bucket')}:</span> <span className="font-num">{probe.bucket || '·'}</span></div>
            <div className="truncate"><span className="opacity-70">{t('admin.probe.prefix')}:</span> <span className="font-num">{probe.prefix || '·'}</span></div>
          </div>
          {Array.isArray(probe.steps) && probe.steps.length > 0 && (
            <ul className="text-[12px] space-y-0.5">
              {probe.steps.map((s, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  {s.ok
                    ? <Check className="h-3 w-3 shrink-0" />
                    : <X className="h-3 w-3 shrink-0" />}
                  <span className="font-medium">{s.label}</span>
                  {typeof s.ms === 'number' && <span className="opacity-60 font-num">{s.ms} ms</span>}
                  {s.ok && s.value && (
                    <span className="opacity-70 truncate font-num">
                      {String(s.value).slice(0, 80)}{String(s.value).length > 80 ? '…' : ''}
                    </span>
                  )}
                  {!s.ok && (
                    <span className="opacity-90">
                      {s.error_kind ? `${s.error_kind}: ` : ''}{s.error}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {probe.error && (
            <div className="text-[12px] opacity-90">{probe.error}</div>
          )}
        </div>
        <button type="button" onClick={onClose}
                className="opacity-60 hover:opacity-100" aria-label={t('common.close') || 'Close'}>
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

/* ---------------------------------- Apps --------------------------------- */

const APP_GROUPS = ['products', 'purchase', 'supply', 'commerce', 'pricing', 'care', 'accounting', 'internal', 'other'];

/* Click-target in the apps table that opens the access list modal · shows
 * the grant count and (for context) the total roster size with a tone
 * change when the app is fully open vs partially open vs locked down. */
const AccessChip = ({ t, count, total, onClick }) => {
  const tone = total > 0 && count === total
    ? 'text-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))]'
    : count === 0
      ? 'text-[hsl(var(--danger))] bg-[hsl(var(--danger-soft))]'
      : 'text-[hsl(var(--text-soft))] bg-[hsl(var(--bg-subtle))] border border-border';
  const label = total > 0 && count === total
    ? t('admin.apps.access.everyone')
    : `${count}/${total}`;
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11.5px] transition hover:opacity-80 ${tone}`}
      title={t('admin.apps.access.viewList')}>
      <Users className="h-3 w-3" />
      <span className="font-num">{label}</span>
    </button>
  );
};

/* Interactive access list for one app · tick / untick employees to
 * grant / revoke. Employees are grouped by department with a tri-state
 * header checkbox per group (all granted = checked, some = indeterminate,
 * none = unchecked) so admins can flip an entire team's access in one
 * click. Every change PATCHes the app via `onPatch` (optimistic with
 * rollback in the parent). A search box narrows the list inline so
 * the modal stays useful even at 50+ employees. */
const AppAccessModal = ({ t, app, employees, onPatch, onClose }) => {
  const { locale } = useLocale();
  const [q, setQ] = useState('');
  const allowed = useMemo(() => new Set(app.allowed_employees || []), [app.allowed_employees]);

  const needle = q.trim().toLowerCase();
  const matches = (e) =>
    !needle
    || (e.full_name || '').toLowerCase().includes(needle)
    || (e.email || '').toLowerCase().includes(needle)
    || (e.departement || '').toLowerCase().includes(needle);

  // Group filtered employees by department, sorted by dept label then
  // by name within each group. "No department" buckets at the end so
  // it doesn't intrude on the team-by-team scan.
  const groups = useMemo(() => {
    const NO_DEPT = '__none__';
    const labelFor = (k) => k === NO_DEPT ? t('splash.team.noDept') : deptLabel(k, locale);
    const buckets = new Map();
    employees.filter(matches).forEach(e => {
      const key = e.departement || NO_DEPT;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(e);
    });
    buckets.forEach(list => list.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')));
    return Array.from(buckets.entries())
      .sort(([a], [b]) => {
        if (a === NO_DEPT) return 1;
        if (b === NO_DEPT) return -1;
        return labelFor(a).localeCompare(labelFor(b));
      })
      .map(([k, list]) => ({ key: k, label: labelFor(k), employees: list }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, needle, locale, t]);

  const grantedCount = useMemo(
    () => employees.reduce((n, e) => n + (allowed.has(e.id) ? 1 : 0), 0),
    [employees, allowed],
  );

  const patchAllowed = (next) => onPatch(app, { allowed_employees: Array.from(next) });

  const toggleEmployee = (empId) => {
    const next = new Set(allowed);
    if (next.has(empId)) next.delete(empId); else next.add(empId);
    patchAllowed(next);
  };

  const setGroup = (group, on) => {
    const next = new Set(allowed);
    for (const e of group.employees) {
      if (on) next.add(e.id); else next.delete(e.id);
    }
    patchAllowed(next);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4"
         style={{ background: 'rgba(20, 30, 25, 0.45)' }}
         onClick={onClose}>
      <div className="w-full max-w-[560px] rounded-xl bg-[hsl(var(--surface))] border border-border shadow-xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}
           role="dialog" aria-modal="true"
           style={{ maxHeight: '80vh' }}>
        <div className="flex items-start justify-between px-5 py-3 border-b border-border gap-3 shrink-0">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-medium text-[hsl(var(--text-muted))]">
              {t('admin.apps.access.modalEyebrow')}
            </div>
            <div className="font-display text-[1.05rem] truncate">{app.name}</div>
            <div className="text-[12px] text-[hsl(var(--text-soft))]">
              {t('admin.apps.access.modalCount', { granted: grantedCount, total: employees.length })}
            </div>
          </div>
          <button type="button" onClick={onClose}
                  className="p-1 -m-1 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                  aria-label={t('common.close') || 'Close'}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-2.5 border-b border-border shrink-0">
          <label className="relative block">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
            <input type="search" value={q} onChange={e => setQ(e.target.value)}
                   placeholder={t('admin.apps.access.modalSearch')}
                   className="input !pl-9 text-[13px] w-full" />
          </label>
        </div>

        <div className="overflow-y-auto">
          {groups.length === 0 ? (
            <div className="px-5 py-8 text-center text-[13px] text-[hsl(var(--text-soft))]">
              {t('admin.apps.f.access.noMatch')}
            </div>
          ) : groups.map(group => {
            const inGroupGranted = group.employees.reduce((n, e) => n + (allowed.has(e.id) ? 1 : 0), 0);
            const allOn  = inGroupGranted === group.employees.length;
            const someOn = inGroupGranted > 0 && !allOn;
            return (
              <div key={group.key} className="border-b border-[hsl(var(--rule))] last:border-b-0">
                <label className="flex items-center gap-2.5 px-5 py-2 bg-[hsl(var(--bg-subtle))] cursor-pointer hover:bg-[hsl(var(--surface-2))]">
                  <input type="checkbox"
                         checked={allOn}
                         ref={el => { if (el) el.indeterminate = someOn; }}
                         onChange={() => setGroup(group, !allOn)} />
                  <span className="text-[10.5px] uppercase tracking-[0.14em] font-medium text-[hsl(var(--text-soft))] flex-1">
                    {group.label}
                  </span>
                  <span className="text-[11px] font-num text-[hsl(var(--text-muted))]">
                    {inGroupGranted}/{group.employees.length}
                  </span>
                </label>
                <ul>
                  {group.employees.map(e => {
                    const checked = allowed.has(e.id);
                    return (
                      <li key={e.id}>
                        <label className="flex items-center gap-2.5 px-5 py-1.5 text-[13px] cursor-pointer hover:bg-[hsl(var(--bg-subtle))]">
                          <input type="checkbox" checked={checked} onChange={() => toggleEmployee(e.id)} />
                          <span className="flex-1 min-w-0 truncate flex items-center gap-1.5">
                            <span className="truncate">{e.full_name || e.email}</span>
                            {e.is_external && (
                              <span className="text-[9px] uppercase tracking-[0.12em] px-1 py-0.5 rounded shrink-0"
                                    style={{ background: 'hsl(var(--bg-subtle))', color: 'hsl(var(--text-soft))' }}>
                                {t('admin.employees.externalBadge')}
                              </span>
                            )}
                          </span>
                          <span className="text-[11.5px] text-[hsl(var(--text-muted))] truncate max-w-[50%]">{e.email}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* Apps × Employees access grid. Rows = apps (already filtered by AppsTab's
 * search/group filter), columns = employees. The first column (app name)
 * stays sticky during horizontal scroll. Every cell is an explicit grant ·
 * tick to give access, untick to revoke. The N/total count per row lets
 * admins see at a glance how widely an app is opened up; the all/none
 * shortcuts apply to the visible employee set (so you can grant "all of
 * commercial" after filtering by department in the global team filter). */
const AppAccessMatrix = ({ t, apps, employees, onPatch }) => {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const visibleEmployees = useMemo(
    () => (needle
      ? employees.filter(e =>
          (e.full_name || '').toLowerCase().includes(needle)
          || (e.email || '').toLowerCase().includes(needle))
      : employees),
    [employees, needle],
  );

  const toggleEmployee = (app, empId, currentlyAllowed) => {
    const allowed = new Set(app.allowed_employees || []);
    if (currentlyAllowed) allowed.delete(empId); else allowed.add(empId);
    onPatch(app, { allowed_employees: Array.from(allowed) });
  };

  const setRowAll = (app, on) => {
    // Apply to the currently-visible employees only · respects the
    // search box, so the admin can grant "everyone matching this
    // search" without losing the rest.
    const allowed = new Set(app.allowed_employees || []);
    for (const e of visibleEmployees) {
      if (on) allowed.add(e.id); else allowed.delete(e.id);
    }
    onPatch(app, { allowed_employees: Array.from(allowed) });
  };

  return (
    <div className="bg-[hsl(var(--surface))] border border-border rounded-lg">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-wrap">
        <p className="text-[12.5px] text-[hsl(var(--text-soft))] flex-1 min-w-0">
          {t('admin.apps.matrix.hint')}
        </p>
        <label className="relative">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('admin.apps.matrix.searchPh')}
            className="input !pl-9 !w-56 text-[13px]"
          />
        </label>
        <span className="text-[12px] text-[hsl(var(--text-muted))]">
          {t('admin.apps.matrix.counts', { apps: apps.length, employees: visibleEmployees.length })}
        </span>
      </div>

      {apps.length === 0 || employees.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">
          {apps.length === 0 ? t('admin.apps.empty') : t('common.loading')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="text-[13px] border-collapse">
            <thead>
              <tr className="bg-[hsl(var(--bg-subtle))] text-[12px] text-[hsl(var(--text-muted))]">
                <th className="sticky left-0 bg-[hsl(var(--bg-subtle))] text-left font-medium px-3 py-2 z-20 border-r border-[hsl(var(--rule))] min-w-[220px]">
                  {t('admin.apps.matrix.col.app')}
                </th>
                {visibleEmployees.map(e => (
                  <th key={e.id}
                      className="font-medium px-1 py-2 text-center min-w-[34px] max-w-[34px]"
                      title={`${e.full_name || ''} · ${e.email || ''}`.trim()}>
                    <div className="text-[10.5px] truncate" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', maxHeight: 110 }}>
                      {e.full_name || e.email}
                    </div>
                  </th>
                ))}
                <th className="px-2 w-full" />
              </tr>
            </thead>
            <tbody>
              {apps.map(app => {
                const allowed = new Set(app.allowed_employees || []);
                const grantedTotal = allowed.size;
                const totalEmployees = employees.length;
                return (
                  <tr key={app.id} className="border-t border-[hsl(var(--rule))]">
                    <td className="sticky left-0 bg-[hsl(var(--surface))] px-3 py-1.5 z-10 border-r border-[hsl(var(--rule))] min-w-[220px]">
                      <div className="flex items-center gap-2 min-w-0" style={{ paddingLeft: app.parent ? 16 : 0 }}>
                        {app.parent
                          ? <CornerDownRight className="h-3 w-3 shrink-0 text-[hsl(var(--text-muted))]" />
                          : app.logo
                            ? <img src={app.logo} alt="" className="h-4 w-4 object-contain shrink-0" />
                            : <span className="h-2 w-2 rounded-full shrink-0" style={{ background: app.dot_color || '#888' }} />}
                        <span className="truncate font-medium flex-1 min-w-0">{app.name}</span>
                        <span className="text-[10.5px] font-num text-[hsl(var(--text-muted))] shrink-0">
                          {grantedTotal}/{totalEmployees}
                        </span>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <button type="button" onClick={() => setRowAll(app, true)}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-border hover:bg-[hsl(var(--bg-subtle))]"
                            title={t('admin.apps.matrix.grantAll')}>
                            {t('admin.apps.matrix.grantAllShort')}
                          </button>
                          <button type="button" onClick={() => setRowAll(app, false)}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-border hover:bg-[hsl(var(--bg-subtle))]"
                            title={t('admin.apps.matrix.revokeAll')}>
                            {t('admin.apps.matrix.revokeAllShort')}
                          </button>
                        </div>
                      </div>
                    </td>
                    {visibleEmployees.map(e => {
                      const checked = allowed.has(e.id);
                      return (
                        <td key={e.id} className="px-1 py-1.5 text-center">
                          <input type="checkbox" checked={checked}
                                 onChange={() => toggleEmployee(app, e.id, checked)}
                                 className="cursor-pointer"
                                 aria-label={`${app.name} · ${e.full_name || e.email}`} />
                        </td>
                      );
                    })}
                    <td />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* Admins act on app access requests here · approve creates the grant
 * on the spot (via the same M2M as the matrix), deny stamps the row
 * with who/when/why. Pending requests sit at the top, history below.
 * Loaded once on mount; refetches after each decision for the latest
 * status. Email notifications fire from the backend; this surface is
 * the in-app counterpart. */
const AccessRequestsTab = ({ t }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('pending');  // 'pending' | 'history' | 'all'

  const load = async () => {
    setError('');
    try { setRows(await api.adminAppAccessRequests()); }
    catch (e) { setError(e.response?.data?.detail || e.message); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (req, decision) => {
    const comment = decision === 'deny'
      ? (window.prompt(t('admin.accessReq.denyPrompt')) || '').trim()
      : '';
    if (decision === 'deny' && !comment) return;
    setBusyId(req.id);
    try {
      const updated = await api.adminDecideAppAccessRequest(req.id, decision, comment);
      setRows(arr => (arr || []).map(r => r.id === req.id ? updated : r));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const visible = useMemo(() => {
    const list = rows || [];
    if (filter === 'pending') return list.filter(r => r.status === 'pending');
    if (filter === 'history') return list.filter(r => r.status !== 'pending');
    return list;
  }, [rows, filter]);

  const counts = useMemo(() => {
    const list = rows || [];
    return {
      pending:  list.filter(r => r.status === 'pending').length,
      history:  list.filter(r => r.status !== 'pending').length,
      all:      list.length,
    };
  }, [rows]);

  return (
    <>
      {error && (
        <div className="mb-4 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
          {error}
        </div>
      )}
      <div className="flex items-center mb-3 gap-3 flex-wrap">
        <p className="text-[13px] text-[hsl(var(--text-soft))] flex-1 min-w-0">
          {t('admin.accessReq.intro')}
        </p>
        <div className="inline-flex rounded-md border border-border overflow-hidden text-[12.5px]">
          {['pending', 'history', 'all'].map(opt => (
            <button key={opt} type="button" onClick={() => setFilter(opt)}
              className={`px-2.5 py-1.5 transition ${filter === opt
                ? 'bg-[hsl(var(--text))] text-[hsl(var(--surface))]'
                : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]'}`}>
              {t(`admin.accessReq.filter.${opt}`)}
              <span className="ml-1 font-num opacity-70">{counts[opt]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
        {rows === null ? (
          <div className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.loading')}</div>
        ) : visible.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('admin.accessReq.empty')}</div>
        ) : (
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {visible.map(req => <AccessRequestRow key={req.id} req={req} t={t}
                                                  busy={busyId === req.id}
                                                  onDecide={decide} />)}
          </ul>
        )}
      </div>
    </>
  );
};

const AccessRequestRow = ({ req, t, busy, onDecide }) => {
  const { locale, intlLocale } = useLocale();
  const fmtWhen = (iso) => iso ? new Intl.DateTimeFormat(intlLocale, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso)) : '·';
  const statusTone = req.status === 'pending'
    ? 'text-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))]'
    : req.status === 'approved'
      ? 'text-[hsl(var(--success))] bg-[hsl(var(--bg-subtle))] border border-[hsl(var(--success)/.3)]'
      : 'text-[hsl(var(--danger))] bg-[hsl(var(--danger-soft))]';
  return (
    <li className="px-4 py-3 flex items-start gap-4">
      <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
            style={{ background: 'hsl(var(--bg-subtle))', border: `1px solid ${(req.app?.dot_color || '#888')}30` }}>
        {req.app?.logo
          ? <img src={req.app.logo} alt="" className="h-full w-full object-contain p-1" />
          : <span className="h-2 w-2 rounded-full" style={{ background: req.app?.dot_color || '#888' }} />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-[14px]">{req.employee?.full_name || req.employee?.email}</span>
          <span className="text-[12.5px] text-[hsl(var(--text-muted))]">→</span>
          <span className="font-medium text-[14px]">{req.app?.name || req.app?.slug}</span>
          {req.employee?.is_external && (
            <span className="text-[9.5px] uppercase tracking-[0.12em] px-1 py-0.5 rounded"
                  style={{ background: 'hsl(var(--bg-subtle))', color: 'hsl(var(--text-soft))' }}>
              {t('admin.employees.externalBadge')}
            </span>
          )}
          <span className={`text-[10.5px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full ml-auto ${statusTone}`}>
            {t(`admin.accessReq.status.${req.status}`)}
          </span>
        </div>
        <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-0.5">
          {req.employee?.email} {req.employee?.departement ? `· ${req.employee.departement}` : ''} · {fmtWhen(req.created_at)}
        </div>
        {req.message && (
          <div className="mt-1.5 border-l-2 border-[hsl(var(--rule-strong))] pl-2.5 text-[12.5px] text-[hsl(var(--text-soft))]">
            {req.message}
          </div>
        )}
        {req.status !== 'pending' && (
          <div className="mt-1.5 text-[11.5px] text-[hsl(var(--text-muted))]">
            {t('admin.accessReq.decidedBy', {
              decision: t(`admin.accessReq.status.${req.status}`),
              who: req.decided_by_email || '·',
              when: fmtWhen(req.decided_at),
            })}
            {req.decision_comment && (
              <> · <span className="italic">"{req.decision_comment}"</span></>
            )}
          </div>
        )}
      </div>
      {req.status === 'pending' && (
        <div className="flex items-center gap-1.5 shrink-0">
          <Button variant="outline" size="sm" disabled={busy}
                  onClick={() => onDecide(req, 'deny')} className="gap-1.5">
            <X className="h-3.5 w-3.5" /> {t('admin.accessReq.deny')}
          </Button>
          <Button size="sm" disabled={busy}
                  onClick={() => onDecide(req, 'approve')} className="gap-1.5">
            <Check className="h-3.5 w-3.5" /> {t('admin.accessReq.approve')}
          </Button>
        </div>
      )}
    </li>
  );
};

/* Auth-probe status dot for one app row, in the Apps table's "Auth"
 * column. Stays a faint idle dot until the admin runs "Check auth"; the
 * colour then encodes the server-side verdict (see backend auth_probe.py):
 *   green  live      · app rejects anonymous requests (Clerk enforcing)
 *   red    open      · endpoint answered anonymously — auth NOT enforced
 *   amber  reachable · app up, expected auth endpoint not found (external)
 *   red ○  down      · could not connect
 *   grey ○ skipped   · no URL to probe (internal route / blank link) */
const AUTH_DOT = {
  live:      { cls: 'bg-[hsl(var(--success))]',                                      key: 'admin.apps.auth.live' },
  open:      { cls: 'bg-[hsl(var(--danger))] ring-2 ring-[hsl(var(--danger)/.25)]',  key: 'admin.apps.auth.open' },
  reachable: { cls: 'bg-[hsl(var(--warning))]',                                      key: 'admin.apps.auth.reachable' },
  down:      { cls: 'border border-[hsl(var(--danger))] bg-transparent',             key: 'admin.apps.auth.down' },
  skipped:   { cls: 'border border-[hsl(var(--text-muted))] bg-transparent',         key: 'admin.apps.auth.skipped' },
};

const AppAuthDot = ({ t, result, probing }) => {
  if (!result) {
    return (
      <span
        className={`inline-block h-2.5 w-2.5 rounded-full bg-[hsl(var(--rule-strong))] ${probing ? 'animate-pulse' : 'opacity-60'}`}
        title={probing ? t('admin.apps.auth.checking') : t('admin.apps.auth.idle')}
        aria-label={probing ? t('admin.apps.auth.checking') : t('admin.apps.auth.idle')}
      />
    );
  }
  const cfg = AUTH_DOT[result.status] || AUTH_DOT.skipped;
  const parts = [t(cfg.key)];
  if (result.auth_status) parts.push(`· ${result.auth_status}`);
  if (typeof result.ms === 'number') parts.push(`· ${result.ms} ms`);
  const label = parts.join(' ');
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full align-middle ${cfg.cls}`}
      title={label}
      aria-label={label}
    />
  );
};

/* Create a module — a restricted page inside an app — without the app form.
 *
 * The full editor asks for fifteen fields (group, icon, colours, links,
 * translations, launch date); none of them mean anything for a row that never
 * appears in the catalogue. This asks two questions instead: which page, and
 * who may open it.
 *
 * The page comes from a dropdown the app itself publishes (GET /api/modules/,
 * see module_discovery.py), not from a slug typed by hand. That mattered more
 * than the typing: the app matches modules by the tail of the slug, so a typo
 * left the page unrestricted while the admin believed they had locked it — a
 * mistake that failed open and was invisible from here.
 *
 * Apps that predate the convention still work: the picker falls back to a
 * free-text key, with the risk spelled out. */
const ModuleCreateModal = ({ t, parent, employees, onCreate, onClose }) => {
  const [discovery, setDiscovery] = useState(null);   // null = still asking
  const [pick, setPick] = useState('');               // key chosen in the list
  const [manualKey, setManualKey] = useState('');
  const [manualLabel, setManualLabel] = useState('');
  const [allowed, setAllowed] = useState(new Set());
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    api.adminAppModules(parent.id)
      .then(d => { if (alive) setDiscovery(d); })
      .catch(() => { if (alive) setDiscovery({ ok: false, reason: 'unreachable', modules: [] }); });
    return () => { alive = false; };
  }, [parent.id]);

  const published = discovery?.ok ? (discovery.modules || []) : [];
  const chosen = published.find(m => m.key === pick);
  // Mirrors what the backend accepts · this becomes the tail of a slug.
  const cleanKey = (manualKey || '')
    .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const key = discovery?.ok ? pick : cleanKey;
  const label = (chosen ? chosen.label : manualLabel).trim() || key;
  const slug = key ? `${parent.slug}-${key}` : '';
  const canSubmit = !!key && !busy;

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await onCreate({
        slug,
        name: label,
        parent: parent.id,
        // Inherited so the row still reads sensibly in the table's Groupe
        // column, even though a module is never grouped in the catalogue.
        group: parent.group || 'other',
        translations: { fr: { name: label, tagline: '', description: '' } },
        allowed_employees: Array.from(allowed),
      });
      onClose();
    } catch (e) {
      const d = e.response?.data;
      setErr(typeof d === 'object' ? JSON.stringify(d) : (d || e.message));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id) => setAllowed(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4"
         style={{ background: 'rgba(20, 30, 25, 0.45)' }} onClick={onClose}>
      <div className="w-full max-w-[560px] rounded-xl bg-[hsl(var(--surface))] border border-border shadow-xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
           style={{ maxHeight: '85vh' }}>
        <div className="flex items-start justify-between px-5 py-3 border-b border-border gap-3 shrink-0">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-medium text-[hsl(var(--text-muted))]">
              {t('admin.apps.module.badge')}
            </div>
            <div className="font-display text-[1.05rem] truncate">
              {t('admin.apps.module.newTitle', { app: parent.name })}
            </div>
          </div>
          <button type="button" onClick={onClose}
                  className="p-1 -m-1 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                  aria-label={t('common.close') || 'Close'}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {discovery === null ? (
            <div className="text-[13px] text-[hsl(var(--text-soft))]">{t('common.loading')}</div>
          ) : discovery.ok ? (
            <div className="field">
              <label className="field-label">{t('admin.apps.module.pick')}</label>
              <select value={pick} onChange={e => setPick(e.target.value)}>
                <option value="">{t('admin.apps.module.pickPh')}</option>
                {published.map(m => (
                  <option key={m.key} value={m.key} disabled={m.exists}>
                    {m.label}{m.exists ? ' · ' + t('admin.apps.module.already') : ''}
                  </option>
                ))}
              </select>
              <p className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1">
                {published.length > 0 && published.every(m => m.exists)
                  ? t('admin.apps.module.noneLeft')
                  : t('admin.apps.module.pickHint')}
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border border-[hsl(var(--warning)/.3)] bg-[hsl(var(--warning-soft))] px-3 py-2 text-[12.5px] text-[hsl(var(--warning))]">
                {t('admin.apps.module.reason.' + discovery.reason)} {t('admin.apps.module.manualHint')}
              </div>
              <div className="field">
                <label className="field-label">{t('admin.apps.module.manualKey')}</label>
                <input className="input font-num" value={manualKey}
                       onChange={e => setManualKey(e.target.value)} placeholder="encodage" />
              </div>
              <div className="field">
                <label className="field-label">{t('admin.apps.module.manualLabel')}</label>
                <input className="input" value={manualLabel}
                       onChange={e => setManualLabel(e.target.value)} placeholder="Encodage" />
              </div>
            </>
          )}

          {slug && (
            <p className="text-[11.5px] font-num text-[hsl(var(--text-muted))]">
              {t('admin.apps.module.slugPreview', { slug })}
            </p>
          )}

          <p className="text-[12.5px] text-[hsl(var(--text-soft))]">
            {t('admin.apps.module.accessHint')}
          </p>
          <AccessSection
            t={t}
            allowed={allowed}
            onToggle={toggle}
            onSetAll={(on) => setAllowed(new Set(on ? employees.map(e => e.id) : []))}
            employees={employees}
            query={query}
            onQueryChange={setQuery}
          />

          {err && <div className="text-[12px] text-[hsl(var(--danger))]">{err}</div>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}>{t('common.cancel')}</Button>
          <Button size="sm" onClick={submit} disabled={!canSubmit} className="gap-1.5">
            <Check className="h-3.5 w-3.5" /> {t('admin.apps.module.create')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AppsTab = ({ t, item, action }) => {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);   // app row · null = creating new
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useQueryState('q');
  const [groupFilter, setGroupFilter] = useQueryState('group');

  const [employees, setEmployees] = useState([]);
  const [view, setView] = useQueryState('view', 'list');  // 'list' | 'matrix'
  const [accessFor, setAccessFor] = useState(null);  // app row · null = closed
  const [addModuleFor, setAddModuleFor] = useState(null);  // parent app · null = closed
  /* Which apps have their modules folded away. Empty by default — an admin
   * opening this table wants to see what exists, not go hunting for it. Kept
   * out of the URL on purpose: it's a per-viewer convenience, so a shared
   * link always arrives fully expanded. */
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleCollapsed = (id) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const navigate = useNavigate();
  // Every open panel is an address, so any of them can be pasted to a
  // colleague: /admin/apps/sangly-encodage/access opens on that page's
  // access list rather than on "go to Apps, find the row, click the pill".
  const go = useCallback(
    (path = '') => navigate(`/admin/apps${path}`),
    [navigate],
  );

  // Auth probe · server-side check of each app's login endpoint. Results
  // are keyed by slug and painted as a dot in the Auth column. Runs once
  // automatically when the tab opens (so the column is never a mystery of
  // blank dots), and the "Check auth" button re-runs it on demand.
  const [authProbe, setAuthProbe] = useState(null);
  const [probingAuth, setProbingAuth] = useState(false);
  const authBySlug = useMemo(() => {
    const m = {};
    (authProbe?.results || []).forEach(r => { m[r.slug] = r; });
    return m;
  }, [authProbe]);
  const runAuthProbe = async () => {
    setProbingAuth(true);
    setError('');
    try { setAuthProbe(await api.appAuthProbe()); }
    catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setProbingAuth(false); }
  };

  const load = async () => {
    try { setRows(await api.adminApps()); }
    catch { setRows([]); }
  };
  useEffect(() => {
    load();
    // Light employee list · only the few fields the access picker needs.
    // Doesn't require admin perms, just an authenticated session.
    api.employees().then(arr => setEmployees(arr || [])).catch(() => setEmployees([]));
    // Populate the Auth column right away · non-blocking, the table renders
    // immediately and the dots fill in (~1s) as the probe returns.
    runAuthProbe();
  }, []);

  /* The address decides what's open, not a click · that's what makes these
   * links shareable, and it keeps the back button honest. Waits for `rows`
   * because a deep link arrives before the catalogue does. */
  useEffect(() => {
    if (!rows) return;
    if (item === 'new') {
      setAdding(true); setEditing({}); setAccessFor(null); setAddModuleFor(null);
      return;
    }
    const row = item ? rows.find(r => r.slug === item) : null;
    if (item && !row) { go(); return; }   // slug renamed, or the row is gone
    setAdding(false);
    setEditing(action ? null : row);
    setAccessFor(action === 'access' ? row : null);
    setAddModuleFor(action === 'module' ? row : null);
  }, [rows, item, action, go]);

  /* PATCH a single app and mirror the change in local state. Used by the
   * matrix cells (toggle one employee in/out of one app's allow list) so
   * each click feels instant without waiting for a full reload. */
  const patchApp = async (app, patch) => {
    setError('');
    const prev = app;
    setRows(rs => (rs || []).map(r => r.id === app.id ? { ...r, ...patch } : r));
    try {
      const updated = await api.adminUpdateApp(app.id, patch);
      setRows(rs => (rs || []).map(r => r.id === app.id ? updated : r));
    } catch (e) {
      // Roll back · the optimistic write was wrong.
      setRows(rs => (rs || []).map(r => r.id === app.id ? prev : r));
      setError(e.response?.data?.detail || e.message);
    }
  };

  const save = async (payload) => {
    setError('');
    try {
      if (editing?.id) {
        await api.adminUpdateApp(editing.id, payload);
      } else {
        await api.adminCreateApp(payload);
      }
      go();
      await load();
    } catch (e) {
      const d = e.response?.data;
      setError(typeof d === 'object' ? JSON.stringify(d) : (d || e.message));
      throw e;
    }
  };

  const togglePatch = async (app, patch) => {
    try {
      await api.adminUpdateApp(app.id, patch);
      setRows(rs => (rs || []).map(r => r.id === app.id ? { ...r, ...patch } : r));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  /* Modules are created from their parent's row rather than from "+ Nouvelle
   * app" · you're declaring a page, not a product, and the app form's fifteen
   * fields are all irrelevant to one. */
  const createModule = async (payload) => {
    await api.adminCreateApp(payload);
    go();
    await load();
  };

  const remove = async (app) => {
    if (!window.confirm(t('admin.apps.confirmDelete', { name: app.name }))) return;
    await api.adminDeleteApp(app.id);
    setRows(rs => (rs || []).filter(r => r.id !== app.id));
    if (item === app.slug) go();   // don't sit on the address of a deleted row
  };

  // Catalogue completeness · counts active apps where every "documented"
  // field is filled so HR can see at a glance which apps still need
  // their copy, owner, launch date or logo. (Always over all active
  // apps · the search box only narrows the table, not the summary.)
  const completenessSummary = useMemo(() => {
    const list = (rows || []).filter(a => a.is_active);
    if (list.length === 0) return null;
    const scored = list.map(a => appCompleteness(a));
    const full = scored.filter(s => s.percent === 100).length;
    const avg = Math.round(scored.reduce((s, x) => s + x.percent, 0) / scored.length);
    return { total: list.length, full, avg };
  }, [rows]);

  const groupOptions = useMemo(() => {
    const seen = new Set();
    (rows || []).forEach(r => { if (r.group) seen.add(r.group); });
    return [...seen].sort();
  }, [rows]);

  // A search or a group filter overrides the folds · see orderedRows.
  const filtering = !!(q.trim() || groupFilter);

  const moduleCounts = useMemo(() => {
    const counts = new Map();
    (rows || []).forEach(r => {
      if (r.parent) counts.set(r.parent, (counts.get(r.parent) || 0) + 1);
    });
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (groupFilter && r.group !== groupFilter) return false;
      if (!needle) return true;
      return [r.name, r.slug, r.group, r.href, r.internal_route, r.owner_email]
        .some(v => (v || '').toLowerCase().includes(needle));
    });
  }, [rows, q, groupFilter]);

  /* Parent-first ordering · every app immediately followed by its modules,
   * so both the table and the matrix read as a tree. A search that hits a
   * module keeps the parent visible for context and vice-versa · finding
   * "encodage" is useless if you can't see which app it belongs to. */
  const orderedRows = useMemo(() => {
    if (!filteredRows) return null;
    const matched = new Set(filteredRows.map(r => r.id));
    const all = rows || [];
    const out = [];
    const accounted = new Set();     // seen here, whether or not it's shown
    for (const app of all.filter(a => !a.parent)) {
      const modules = all.filter(m => m.parent === app.id);
      const parentMatched = matched.has(app.id);
      const shownModules = modules.filter(m => parentMatched || matched.has(m.id));
      if (!parentMatched && shownModules.length === 0) continue;
      out.push(app); accounted.add(app.id);
      // A fold never hides a search result · you asked to find something.
      const folded = !filtering && collapsed.has(app.id);
      for (const m of shownModules) {
        accounted.add(m.id);
        if (!folded) out.push(m);
      }
    }
    // Safety net · a module whose parent got filtered out of `rows` entirely
    // must still show up rather than vanish without a trace.
    for (const r of filteredRows) if (!accounted.has(r.id)) out.push(r);
    return out;
  }, [rows, filteredRows, collapsed, filtering]);

  return (
    <>
      {error && (
        <div className="mb-4 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
          {error}
        </div>
      )}

      {/* On its own line · inside the toolbar row it was the only flexible item
          next to six fixed controls, so it collapsed into a one-word column. */}
      <p className="text-[13px] text-[hsl(var(--text-soft))] mb-3 max-w-[80ch]">
        {t('admin.apps.intro')}
      </p>

      <div className="flex items-center mb-3 gap-3 flex-wrap">
        <label className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('admin.apps.searchPh') || 'Rechercher…'}
            className="input !pl-9 !w-56 text-[13px]"
          />
        </label>
        <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
                className="input !w-auto text-[13px]">
          <option value="">{t('admin.apps.allGroups') || 'Tous les groupes'}</option>
          {groupOptions.map(g => <option key={g} value={g}>{groupLabel(t, g)}</option>)}
        </select>
        {completenessSummary && (
          <CompletenessSummary t={t} summary={completenessSummary} />
        )}
        <div className="inline-flex rounded-md border border-border overflow-hidden text-[12.5px]">
          <button type="button" onClick={() => setView('list')}
            className={`px-3 py-1.5 transition ${view === 'list'
              ? 'bg-[hsl(var(--text))] text-[hsl(var(--surface))]'
              : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]'}`}>
            {t('admin.apps.view.list')}
          </button>
          <button type="button" onClick={() => setView('matrix')}
            className={`px-3 py-1.5 transition ${view === 'matrix'
              ? 'bg-[hsl(var(--text))] text-[hsl(var(--surface))]'
              : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]'}`}>
            {t('admin.apps.view.matrix')}
          </button>
        </div>
        {view === 'list' && (
          <button
            type="button"
            onClick={runAuthProbe}
            disabled={probingAuth}
            title={authProbe?.checked_at
              ? `${t('admin.apps.auth.checkedAt')} ${new Date(authProbe.checked_at).toLocaleTimeString()}`
              : undefined}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-[12.5px] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text))] hover:border-[hsl(var(--rule-strong))] transition disabled:opacity-50"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {probingAuth ? t('admin.apps.auth.checking') : t('admin.apps.auth.check')}
          </button>
        )}
        {!editing && !adding && view === 'list' && (
          <Button size="sm" onClick={() => go('/new')} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> {t('admin.apps.new')}
          </Button>
        )}
      </div>

      {view === 'matrix' && (
        <AppAccessMatrix
          t={t}
          apps={(orderedRows || []).filter(a => a.is_active)}
          employees={employees}
          onPatch={patchApp}
        />
      )}

      {view === 'list' && editing && (
        <AppEditor t={t} app={editing} apps={rows || []} employees={employees}
                   onSave={save} onCancel={() => go()} />
      )}

      {view === 'list' && (
      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
        <table className="w-full text-[13.5px] table-fixed">
          <colgroup>
            <col className="w-[5%]"  />{/* Volgorde */}
            <col className="w-[20%]" />{/* Naam */}
            <col className="w-[10%]" />{/* Groep */}
            <col className="w-[10%]" />{/* Auteur (owner first name) */}
            <col className="w-[5%]"  />{/* Link (icon) */}
            <col className="w-[9%]"  />{/* Auth · Clerk dot + manual access flag */}
            <col           />{/* Volledigheid · absorbs slack */}
            <col className="w-[10%]" />{/* Toegang */}
            <col className="w-[6%]"  />{/* Uitgelicht */}
            <col className="w-[6%]"  />{/* Actief */}
            <col className="w-[6%]"  />{/* Actions */}
          </colgroup>
          <thead className="bg-[hsl(var(--bg-subtle))] text-[12.5px] text-[hsl(var(--text-muted))] border-b border-border">
            <tr>
              <th className="text-left font-medium px-3 py-2.5">{t('admin.apps.col.sort')}</th>
              <th className="text-left font-medium px-3 py-2.5">{t('admin.apps.col.name')}</th>
              <th className="text-left font-medium px-3 py-2.5">{t('admin.apps.col.group')}</th>
              <th className="text-left font-medium px-3 py-2.5">{t('admin.apps.col.owner')}</th>
              <th className="text-center font-medium px-2 py-2.5" aria-label={t('admin.apps.col.target')} />
              <th className="text-center font-medium px-2 py-2.5" title={t('admin.apps.col.authHint')}>{t('admin.apps.col.auth')}</th>
              <th className="text-left font-medium px-3 py-2.5">{t('admin.apps.col.complete')}</th>
              <th className="text-left font-medium px-3 py-2.5">{t('admin.apps.col.access')}</th>
              <th className="text-center font-medium px-2 py-2.5">{t('admin.apps.col.featured')}</th>
              <th className="text-center font-medium px-2 py-2.5">{t('admin.apps.col.active')}</th>
              <th className="px-2" />
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.loading')}</td></tr>
            )}
            {rows !== null && (orderedRows || []).length === 0 && (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">
                {(q || groupFilter) ? (t('admin.apps.noMatch') || t('common.empty')) : t('admin.apps.empty')}
              </td></tr>
            )}
            {(orderedRows || []).map(app => (
              <tr key={app.id} className="border-t border-[hsl(var(--rule))] hover:bg-[hsl(var(--surface-2))]">
                <td className="px-3 py-2 font-num text-[12.5px] text-[hsl(var(--text-muted))]">{app.sort_order}</td>
                <td className="px-3 py-2 min-w-0">
                  <div className="flex items-start gap-1 min-w-0">
                    {!app.parent && (moduleCounts.get(app.id) ? (
                      <button type="button" onClick={() => toggleCollapsed(app.id)}
                              aria-expanded={!collapsed.has(app.id)}
                              title={collapsed.has(app.id)
                                ? t('admin.apps.module.expand', { n: moduleCounts.get(app.id) })
                                : t('admin.apps.module.collapse')}
                              className="mt-0.5 -ml-1 p-0.5 shrink-0 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${
                          collapsed.has(app.id) ? '-rotate-90' : ''}`} />
                      </button>
                    ) : (
                      /* Keeps every app name on the same left edge, folded or not. */
                      <span className="w-3.5 shrink-0" aria-hidden />
                    ))}
                    <div className={`min-w-0 flex-1 ${
                      app.parent ? 'pl-3 ml-1 border-l-2 border-[hsl(var(--rule))]' : ''}`}>
                      <button type="button" onClick={() => go(`/${app.slug}`)}
                              className="flex items-center gap-1 text-left font-medium hover:underline w-full min-w-0">
                        {app.parent && (
                          <CornerDownRight className="h-3 w-3 shrink-0 text-[hsl(var(--text-muted))]" />
                        )}
                        <span className="truncate">{app.name}</span>
                        {collapsed.has(app.id) && !filtering && (
                          <span className="shrink-0 text-[10px] font-num px-1 py-0.5 rounded-full"
                                style={{ background: 'hsl(var(--bg-subtle))', color: 'hsl(var(--text-soft))' }}>
                            +{moduleCounts.get(app.id)}
                          </span>
                        )}
                      </button>
                      <div className="text-[11px] font-num text-[hsl(var(--text-muted))] truncate">{app.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-[12.5px] text-[hsl(var(--text-soft))] truncate">
                  {app.parent ? (
                    <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded"
                          style={{ background: 'hsl(var(--bg-subtle))', color: 'hsl(var(--text-soft))' }}
                          title={t('admin.apps.module.hint')}>
                      {t('admin.apps.module.badge')}
                    </span>
                  ) : groupLabel(t, app.group)}
                </td>
                <td className="px-3 py-2 text-[12.5px] text-[hsl(var(--text-soft))] truncate">
                  {(() => {
                    const owner = (employees || []).find(
                      e => (e.email || '').toLowerCase() === (app.owner_email || '').toLowerCase(),
                    );
                    if (owner) {
                      // Prefer first name · keeps the column compact.
                      // Falls back to full_name when first_name is empty
                      // (legacy rows from older imports).
                      const label = (owner.first_name || (owner.full_name || '').split(' ')[0] || owner.email);
                      return <span title={owner.email}>{label}</span>;
                    }
                    if (app.owner_email) {
                      // Email-only fallback when there's no matching
                      // employee row · still useful to know who to ping.
                      return <span className="font-num text-[11.5px] text-[hsl(var(--text-muted))] truncate"
                                   title={app.owner_email}>
                        {app.owner_email.split('@')[0]}
                      </span>;
                    }
                    return <span className="text-[hsl(var(--text-muted))]">·</span>;
                  })()}
                </td>
                <td className="px-1 py-2 text-center">
                  {app.internal_route ? (
                    <Link to={app.internal_route}
                          className="inline-flex p-1 rounded text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                          title={app.internal_route}>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  ) : app.href ? (
                    <a href={app.href} target="_blank" rel="noreferrer"
                       className="inline-flex p-1 rounded text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                       title={app.href}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span className="text-[hsl(var(--text-muted))]">·</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center justify-center gap-2">
                    <AppAuthDot t={t} result={authBySlug[app.slug]} probing={probingAuth} />
                    <button
                      type="button"
                      onClick={() => togglePatch(app, { access_verified: !app.access_verified })}
                      aria-pressed={!!app.access_verified}
                      title={app.access_verified
                        ? t('admin.apps.access.verifiedOn')
                        : t('admin.apps.access.verifiedOff')}
                      className={`inline-flex items-center justify-center h-4 w-4 rounded-full border transition ${
                        app.access_verified
                          ? 'border-[hsl(var(--success))] bg-[hsl(var(--success))] text-white'
                          : 'border-[hsl(var(--rule-strong))] text-transparent hover:border-[hsl(var(--text))]'}`}
                    >
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <CompletenessBar t={t} score={appCompleteness(app)} />
                </td>
                <td className="px-3 py-2">
                  <AccessChip t={t}
                              count={(app.allowed_employees || []).length}
                              total={employees.length}
                              onClick={() => go(`/${app.slug}/access`)} />
                </td>
                <td className="px-2 py-2 text-center">
                  {app.parent
                    ? <span className="text-[hsl(var(--text-muted))]" title={t('admin.apps.module.notFeatured')}>·</span>
                    : <input type="checkbox" checked={!!app.is_featured}
                             onChange={(e) => togglePatch(app, { is_featured: e.target.checked })} />}
                </td>
                <td className="px-2 py-2 text-center">
                  <input type="checkbox" checked={!!app.is_active}
                         onChange={(e) => togglePatch(app, { is_active: e.target.checked })} />
                </td>
                <td className="px-2 py-2 text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    {!app.parent && (
                      <button type="button" onClick={() => go(`/${app.slug}/module`)}
                              className="p-1 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--accent))]"
                              title={t('admin.apps.module.add')}>
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button type="button" onClick={() => go(`/${app.slug}`)}
                            className="p-1 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                            title={t('common.edit')}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={() => remove(app)}
                            className="p-1 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]"
                            title={t('common.delete')}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {addModuleFor && (
        <ModuleCreateModal
          t={t}
          parent={addModuleFor}
          employees={employees}
          onCreate={createModule}
          onClose={() => go()}
        />
      )}

      {accessFor && (
        <AppAccessModal
          t={t}
          /* Re-read the row from `rows` on every render so the modal
             reflects the latest grants right after a checkbox flip ·
             `accessFor` is a snapshot from the moment the modal opened. */
          app={(rows || []).find(r => r.id === accessFor.id) || accessFor}
          employees={employees}
          onPatch={patchApp}
          onClose={() => go()}
        />
      )}
    </>
  );
};

const APP_LANGS = ['fr', 'nl', 'en'];
const emptyAppTranslations = () => ({
  fr: { name: '', tagline: '', description: '' },
  nl: { name: '', tagline: '', description: '' },
  en: { name: '', tagline: '', description: '' },
});

/* Admin's "is this app documented?" score · 8 buckets that together
 * make up a complete catalogue entry. Returns the percent + the list
 * of missing keys (i18n keys, resolved by the row component). */
const COMPLETENESS_FIELDS = [
  ['description', a => !!(a.description || '').trim()],
  ['translations.fr', a => !!(a.translations?.fr?.name || '').trim() && !!(a.translations?.fr?.description || '').trim()],
  ['translations.nl', a => !!(a.translations?.nl?.name || '').trim() && !!(a.translations?.nl?.description || '').trim()],
  ['translations.en', a => !!(a.translations?.en?.name || '').trim() && !!(a.translations?.en?.description || '').trim()],
  ['owner_email',   a => !!(a.owner_email || '').trim()],
  ['doc_url',       a => !!(a.doc_url || '').trim()],
  ['released_at',   a => !!a.released_at],
  ['logo',          a => !!a.logo],
];

const appCompleteness = (app) => {
  const missing = [];
  let hit = 0;
  for (const [key, test] of COMPLETENESS_FIELDS) {
    if (test(app)) hit += 1;
    else missing.push(key);
  }
  return { percent: Math.round((hit / COMPLETENESS_FIELDS.length) * 100), missing };
};

const CompletenessBar = ({ t, score }) => {
  const tone = score.percent === 100
    ? 'hsl(var(--success))'
    : score.percent >= 60
      ? '#F4C645'
      : 'hsl(var(--danger))';
  // Missing-list lives in the title attribute · keeps the row a single
  // line so the apps table fits all columns without horizontal squeeze.
  const missingLabel = score.missing.length
    ? `${t('admin.apps.complete.missing')}: ${score.missing.map(k => t(`admin.apps.complete.k.${k}`) || k).join(', ')}`
    : t('admin.apps.complete.ok');
  return (
    <div title={missingLabel} className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-[hsl(var(--bg-subtle))]">
        <div className="h-full" style={{ width: `${score.percent}%`, background: tone }} />
      </div>
      <span className="text-[11px] font-num text-[hsl(var(--text-soft))] w-9 text-right">{score.percent}%</span>
    </div>
  );
};

const CompletenessSummary = ({ t, summary }) => (
  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-[12.5px] bg-[hsl(var(--surface))]">
    <span className="font-num font-medium">{summary.full}/{summary.total}</span>
    <span className="text-[hsl(var(--text-soft))]">{t('admin.apps.complete.fullSummary')}</span>
    <span className="text-[hsl(var(--text-muted))]">·</span>
    <span className="font-num">{summary.avg}%</span>
    <span className="text-[hsl(var(--text-soft))]">{t('admin.apps.complete.avg')}</span>
  </span>
);

/* Logo uploader for the app catalogue · only available once the app
 * exists (we need its id for the PATCH endpoint). Optimistically swaps
 * the preview to the local data URL until the server URL comes back. */
const AppLogoField = ({ app, t }) => {
  const [logoUrl, setLogoUrl] = useState(app.logo || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setBusy(true);
    const preview = URL.createObjectURL(file);
    setLogoUrl(preview);
    try {
      const updated = await api.adminUploadAppLogo(app.id, file);
      setLogoUrl(updated.logo || null);
    } catch (err) {
      setError(err.response?.data?.detail || t('admin.apps.logo.failed') || 'Upload failed');
      setLogoUrl(app.logo || null);
    } finally {
      setBusy(false);
      URL.revokeObjectURL(preview);
      e.target.value = '';
    }
  };

  const clear = async () => {
    if (!logoUrl) return;
    setError(''); setBusy(true);
    try {
      const updated = await api.adminClearAppLogo(app.id);
      setLogoUrl(updated.logo || null);
    } catch (err) {
      setError(err.response?.data?.detail || t('admin.apps.logo.failed') || 'Clear failed');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <label className="field-label">{t('admin.apps.f.logo')}</label>
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-md border border-border flex items-center justify-center overflow-hidden bg-[hsl(var(--bg-subtle))]">
          {logoUrl
            ? <img src={logoUrl} alt="" className="h-full w-full object-contain" />
            : <span className="text-[10px] text-[hsl(var(--text-muted))] uppercase tracking-wider">—</span>}
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] border border-border rounded-md cursor-pointer hover:bg-[hsl(var(--surface-2))]">
            <input type="file" accept="image/*" onChange={pick} disabled={busy} className="hidden" />
            {busy ? t('common.saving') : (logoUrl ? t('admin.apps.logo.replace') : t('admin.apps.logo.upload'))}
          </label>
          {logoUrl && (
            <button type="button" onClick={clear} disabled={busy}
                    className="text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]">
              {t('common.delete')}
            </button>
          )}
        </div>
      </div>
      {error && <div className="text-[12px] text-[hsl(var(--danger))] mt-1.5">{error}</div>}
      <p className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1.5">{t('admin.apps.logo.hint')}</p>
    </div>
  );
};

/* Access controls for the App editor · spans both columns of the form
 * grid (md:col-span-2). Every app needs explicit grants · tick the
 * employees who should see it. Bulk all/none buttons let admins set
 * the whole roster in one click. */
const AccessSection = ({ t, allowed, onToggle, onSetAll, employees, query, onQueryChange }) => {
  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? employees.filter(e =>
        (e.full_name || '').toLowerCase().includes(needle)
        || (e.email || '').toLowerCase().includes(needle))
    : employees;
  return (
    <div className="field md:col-span-2 rounded-md border border-border p-3 bg-[hsl(var(--bg-subtle))]">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div className="text-[13px] font-medium text-[hsl(var(--text-soft))]">
          {t('admin.apps.f.access')}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => onSetAll(true)}
            className="text-[11.5px] px-2 py-0.5 rounded-md border border-border hover:bg-[hsl(var(--surface))]">
            {t('admin.apps.f.access.grantAll')}
          </button>
          <button type="button" onClick={() => onSetAll(false)}
            className="text-[11.5px] px-2 py-0.5 rounded-md border border-border hover:bg-[hsl(var(--surface))]">
            {t('admin.apps.f.access.revokeAll')}
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <input type="search" className="input flex-1"
               placeholder={t('admin.apps.f.access.search')}
               value={query} onChange={e => onQueryChange(e.target.value)} />
        <span className="text-[12px] text-[hsl(var(--text-muted))] shrink-0">
          {t('admin.apps.f.access.countOf', { n: allowed.size, total: employees.length })}
        </span>
      </div>
      <div className="max-h-56 overflow-y-auto rounded-md border border-border bg-[hsl(var(--surface))]">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-[12.5px] text-[hsl(var(--text-muted))]">
            {employees.length === 0 ? t('common.loading') : t('admin.apps.f.access.noMatch')}
          </div>
        ) : (
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {filtered.map(e => {
              const checked = allowed.has(e.id);
              return (
                <li key={e.id}>
                  <label className="flex items-center gap-2.5 px-3 py-2 text-[13px] cursor-pointer hover:bg-[hsl(var(--bg-subtle))]">
                    <input type="checkbox" checked={checked} onChange={() => onToggle(e.id)} />
                    <span className="flex-1 min-w-0 truncate flex items-center gap-1.5">
                      <span className="truncate">{e.full_name || e.email}</span>
                      {e.is_external && (
                        <span className="text-[9px] uppercase tracking-[0.12em] px-1 py-0.5 rounded shrink-0"
                              style={{ background: 'hsl(var(--bg-subtle))', color: 'hsl(var(--text-soft))' }}>
                          {t('admin.employees.externalBadge')}
                        </span>
                      )}
                    </span>
                    <span className="text-[11.5px] text-[hsl(var(--text-muted))] truncate max-w-[40%]">{e.email}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const AppEditor = ({ t, app, apps = [], employees = [], onSave, onCancel }) => {
  // Hydrate translations from the row · FR doubles as the canonical name
  // and description, so the existing values migrate cleanly.
  const initialTranslations = () => {
    const base = emptyAppTranslations();
    const tr = app.translations || {};
    for (const lang of APP_LANGS) {
      base[lang] = {
        name:        tr[lang]?.name        ?? (lang === 'fr' ? (app.name || '') : ''),
        tagline:     tr[lang]?.tagline     ?? '',
        description: tr[lang]?.description ?? (lang === 'fr' ? (app.description || '') : ''),
      };
    }
    return base;
  };
  const [draft, setDraft] = useState({
    slug: app.slug || '',
    // Empty = a catalogue app. Set = this row is a restricted page inside
    // that app instead (see App.parent).
    parent: app.parent || null,
    group: app.group || 'other',
    icon_name: app.icon_name || 'Briefcase',
    dot_color: app.dot_color || '#2B4138',
    tint_color: app.tint_color || 'rgba(43,65,56,0.08)',
    href: app.href || '',
    internal_route: app.internal_route || '',
    owner_email: app.owner_email || '',
    contact_email: app.contact_email || '',
    doc_url: app.doc_url || '',
    released_at: app.released_at || '',
    is_featured: !!app.is_featured,
    is_active: app.is_active != null ? app.is_active : true,
    access_verified: !!app.access_verified,
    sort_order: app.sort_order != null ? app.sort_order : 100,
    // Allowed-employee picker stores IDs as a Set for cheap toggling;
    // serialised to an array on submit so DRF can persist the M2M.
    allowed_employees: new Set(app.allowed_employees || []),
    translations: initialTranslations(),
  });
  // Only real apps can be a parent · nesting is one level deep, and an app
  // obviously can't be its own. The API re-checks both.
  const parentOptions = useMemo(
    () => (apps || [])
      .filter(a => !a.parent && a.id !== app.id)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [apps, app.id],
  );
  const [employeeQuery, setEmployeeQuery] = useState('');
  const toggleAllowed = (empId) => setDraft(d => {
    const next = new Set(d.allowed_employees);
    if (next.has(empId)) next.delete(empId); else next.add(empId);
    return { ...d, allowed_employees: next };
  });
  const setAllAllowed = (on) => setDraft(d => ({
    ...d,
    allowed_employees: new Set(on ? employees.map(e => e.id) : []),
  }));
  const [activeLang, setActiveLang] = useState('fr');
  const [busy, setBusy] = useState(false);
  const set = (patch) => setDraft(d => ({ ...d, ...patch }));
  const setLangField = (field, value) => setDraft(d => ({
    ...d,
    translations: { ...d.translations, [activeLang]: { ...d.translations[activeLang], [field]: value } },
  }));
  const current = draft.translations[activeLang] || { name: '', tagline: '', description: '' };

  const submit = async () => {
    setBusy(true);
    try {
      // FR doubles as the canonical name/description for the backend column
      // (and for legacy readers without translation support).
      const fr = draft.translations.fr || { name: '', tagline: '', description: '' };
      // Strip empty translations so the JSON stays clean.
      const trimmed = {};
      for (const lang of APP_LANGS) {
        const tr = draft.translations[lang] || {};
        if ((tr.name || '').trim() || (tr.tagline || '').trim() || (tr.description || '').trim()) {
          trimmed[lang] = {
            name: tr.name || '',
            tagline: tr.tagline || '',
            description: tr.description || '',
          };
        }
      }
      await onSave({
        ...draft,
        name: fr.name.trim() || draft.slug,
        description: fr.description || '',
        translations: trimmed,
        released_at: draft.released_at || null,
        parent: draft.parent || null,
        // A module never belongs in the mega-menu · mirrors App.save so the
        // form can't send a value the model would silently drop.
        is_featured: draft.parent ? false : draft.is_featured,
        allowed_employees: Array.from(draft.allowed_employees),
      });
    } catch { /* error surfaced by parent */ }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-display text-[1rem]">
          {app.id ? t('admin.apps.editTitle') : t('admin.apps.newTitle')}
        </span>
        <button type="button" onClick={onCancel} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="field">
          <label className="field-label">{t('admin.apps.f.slug')}</label>
          <input className="input font-num" value={draft.slug}
                 onChange={e => set({ slug: e.target.value })}
                 disabled={!!app.id} placeholder="perfy" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.parent')}</label>
          <select value={draft.parent || ''}
                  onChange={e => set({ parent: e.target.value ? Number(e.target.value) : null })}>
            <option value="">{t('admin.apps.f.parent.none')}</option>
            {parentOptions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <p className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1">
            {t('admin.apps.f.parent.help')}
          </p>
        </div>
        <div className="field md:col-span-2">
          {/* Language tabs · FR is canonical, NL/EN are optional. */}
          <div className="flex items-center gap-1 border-b border-border mb-2">
            {APP_LANGS.map(lang => {
              const tr = draft.translations[lang] || {};
              const filled = !!((tr.name || '').trim() || (tr.description || '').trim());
              const isActive = lang === activeLang;
              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setActiveLang(lang)}
                  className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] transition ${
                    isActive
                      ? 'text-[hsl(var(--text))] font-medium border-b-2 -mb-px border-[hsl(var(--accent))]'
                      : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]'
                  }`}
                >
                  <span className="uppercase font-num text-[11px]">{lang}</span>
                  <span>{t(`composer.lang.${lang}`)}</span>
                  {lang === 'fr' && <span className="text-[10px] opacity-70">★</span>}
                  {filled && lang !== 'fr' && <Check className="h-3 w-3 text-[hsl(var(--accent))]" />}
                </button>
              );
            })}
          </div>
          <label className="field-label">
            {t('admin.apps.f.name')}
            {activeLang !== 'fr' && (
              <span className="ml-2 text-[11px] text-[hsl(var(--text-muted))]">
                {t('composer.lang.optional')}
              </span>
            )}
          </label>
          <input className="input" value={current.name}
                 onChange={e => setLangField('name', e.target.value)} placeholder="Perfy" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.group')}</label>
          <select value={draft.group} onChange={e => set({ group: e.target.value })}>
            {APP_GROUPS.map(g => <option key={g} value={g}>{groupLabel(t, g)}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.iconName')}</label>
          <input className="input font-num" value={draft.icon_name}
                 onChange={e => set({ icon_name: e.target.value })} placeholder="Briefcase" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.href')}</label>
          <input className="input font-num" value={draft.href}
                 onChange={e => set({ href: e.target.value })} placeholder="https://…" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.internal')}</label>
          <input className="input font-num" value={draft.internal_route}
                 onChange={e => set({ internal_route: e.target.value })} placeholder="/home" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.dot')}</label>
          <input className="input font-num" value={draft.dot_color}
                 onChange={e => set({ dot_color: e.target.value })} placeholder="#2B4138" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.tint')}</label>
          <input className="input font-num" value={draft.tint_color}
                 onChange={e => set({ tint_color: e.target.value })} placeholder="rgba(43,65,56,0.08)" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.owner')}</label>
          <input className="input" value={draft.owner_email}
                 onChange={e => set({ owner_email: e.target.value })} placeholder="owner@lamptwist.be" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.contact')}</label>
          <input className="input" value={draft.contact_email}
                 onChange={e => set({ contact_email: e.target.value })} placeholder="support@lamptwist.be" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.doc')}</label>
          <input className="input font-num" value={draft.doc_url}
                 onChange={e => set({ doc_url: e.target.value })} placeholder="https://…" />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.released')}</label>
          <input type="date" className="input font-num" value={draft.released_at || ''}
                 onChange={e => set({ released_at: e.target.value })} />
        </div>
        {app.id && (
          <div className="field md:col-span-2">
            <AppLogoField app={app} t={t} />
          </div>
        )}
        <div className="field md:col-span-2">
          <label className="field-label">
            {t('admin.apps.f.tagline')}
            <span className="ml-2 text-[11px] text-[hsl(var(--text-muted))] uppercase">{activeLang}</span>
          </label>
          <input type="text" value={current.tagline || ''} maxLength={140}
                 placeholder={t('admin.apps.f.tagline.ph')}
                 onChange={e => setLangField('tagline', e.target.value)} />
          <p className="text-[12px] text-[hsl(var(--text-muted))] mt-1">
            {t('admin.apps.f.tagline.help')}
          </p>
        </div>
        <div className="field md:col-span-2">
          <label className="field-label">
            {t('admin.apps.f.description')}
            <span className="ml-2 text-[11px] text-[hsl(var(--text-muted))] uppercase">{activeLang}</span>
          </label>
          <textarea rows={4} value={current.description}
                    onChange={e => setLangField('description', e.target.value)} />
        </div>
        <div className="field">
          <label className="field-label">{t('admin.apps.f.sort')}</label>
          <input type="number" className="input font-num" value={draft.sort_order}
                 onChange={e => set({ sort_order: Number(e.target.value) || 0 })} />
        </div>
        <div className="field flex items-end gap-4">
          <label className={`inline-flex items-center gap-2 text-[14px] ${draft.parent ? 'opacity-50' : ''}`}
                 title={draft.parent ? t('admin.apps.module.notFeatured') : undefined}>
            <input type="checkbox" checked={draft.is_featured && !draft.parent}
                   disabled={!!draft.parent}
                   onChange={e => set({ is_featured: e.target.checked })} />
            {t('admin.apps.f.featured')}
          </label>
          <label className="inline-flex items-center gap-2 text-[14px]">
            <input type="checkbox" checked={draft.is_active}
                   onChange={e => set({ is_active: e.target.checked })} />
            {t('admin.apps.f.active')}
          </label>
          <label className="inline-flex items-center gap-2 text-[14px]"
                 title={t('admin.apps.access.verifiedHint')}>
            <input type="checkbox" checked={draft.access_verified}
                   onChange={e => set({ access_verified: e.target.checked })} />
            {t('admin.apps.f.accessVerified')}
          </label>
        </div>
        <AccessSection
          t={t}
          allowed={draft.allowed_employees}
          onToggle={toggleAllowed}
          onSetAll={setAllAllowed}
          employees={employees}
          query={employeeQuery}
          onQueryChange={setEmployeeQuery}
        />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button size="sm" onClick={submit} disabled={busy} className="gap-1.5">
          <Check className="h-3.5 w-3.5" /> {t('admin.apps.save')}
        </Button>
      </div>
    </div>
  );
};

export default Admin;
